import React, { useState, useMemo, useRef, lazy, Suspense } from 'react'
import BakiyeWidget from '../BakiyeWidget'
const MapWidget = lazy(() => import('./MapWidget'))
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  AreaChart, Area, ScatterChart, Scatter, Treemap, ZAxis,
  ComposedChart, LabelList,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Brush,
} from 'recharts'
import { FiChevronUp, FiChevronDown, FiSearch, FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import LojDetayModal from '../LojDetayModal'
import FirmaCariModal from '../FirmaCariModal'

// COLORS - getPalette()'ye proxy. .length ve [i] erisimini destekler.
const COLORS = new Proxy({}, {
  get(_, prop) {
    const p = getPalette()
    if (prop === 'length') return p.length
    if (prop === Symbol.iterator) return p[Symbol.iterator].bind(p)
    if (prop === 'map' || prop === 'filter' || prop === 'forEach' || prop === 'slice' || prop === 'find' || prop === 'indexOf') return p[prop].bind(p)
    const idx = Number(prop)
    return Number.isInteger(idx) ? p[idx] : undefined
  }
})

const truncateLabel = (str, max = 18) => {
  if (!str) return ''
  const s = String(str)
  return s.length > max ? s.substring(0, max) + '...' : s
}

const AY_ADLARI = {1:'Ocak',2:'Subat',3:'Mart',4:'Nisan',5:'Mayis',6:'Haziran',7:'Temmuz',8:'Agustos',9:'Eylul',10:'Ekim',11:'Kasim',12:'Aralik'}

// Pocan istasyon etiketi/rengi — backend eski snapshot'larda BUYUK HARF (ALTINEKIN) gonderebilir, normalize et
const ISTASYON_LABEL = (s) => {
  const x = (s || '').toLocaleLowerCase('tr-TR').replace(/ı/g, 'i')
  if (x.includes('altin')) return 'Altınekin'
  if (x.includes('tatli')) return 'Tatlıcak'
  if (x.includes('ladik')) return 'Ladık'
  return s || ''
}
const ISTASYON_COLOR = (s) => {
  const x = (s || '').toLocaleLowerCase('tr-TR').replace(/ı/g, 'i')
  if (x.includes('altin')) return 'text-blue-600 dark:text-blue-400'
  if (x.includes('tatli')) return 'text-emerald-600 dark:text-emerald-400'
  if (x.includes('ladik')) return 'text-amber-600 dark:text-amber-400'
  return 'text-gray-500 dark:text-gray-400'
}
// Snapshot guncelleme zamani -> "X dk once" (kullanici verinin tazeligini gorsun)
const ZAMAN_ONCE = (iso) => {
  if (!iso) return ''
  const t = new Date(iso)
  if (isNaN(t.getTime())) return ''
  const dk = Math.floor((Date.now() - t.getTime()) / 60000)
  if (dk < 1) return 'az önce'
  if (dk < 60) return `${dk} dk önce`
  const sa = Math.floor(dk / 60)
  if (sa < 24) return `${sa} sa önce`
  return t.toLocaleDateString('tr-TR')
}

// X ekseni icin akilli tarih/ay sort anahtari
// - Number => 0..N
// - "2026-03" / "2026/03" / "2026-03-15" => YYYYMMDD
// - "Mayis 2025" / "Mayıs 2025" => YYYYMM (TR ay isimleri)
// - "2025-Mayis" => YYYYMM
// - Date.parse parse edebiliyorsa => epoch
// - Aksi halde null donduru (string sort cagiran tarafta yapilir)
const TR_AY_MAP = { ocak:1, subat:2, "şubat":2, mart:3, nisan:4, mayis:5, "mayıs":5, haziran:6, temmuz:7, agustos:8, "ağustos":8, eylul:9, "eylül":9, ekim:10, kasim:11, "kasım":11, aralik:12, "aralık":12 }
const xSortKey = (val) => {
  if (val == null || val === '') return null
  const n = Number(val)
  if (!isNaN(n) && val !== '' && !isNaN(parseFloat(val))) return n
  const s = String(val).trim()
  // 2026-03-15 / 2026-03 / 2026/03
  let m = s.match(/^(\d{4})[-\/](\d{1,2})(?:[-\/](\d{1,2}))?/)
  if (m) return Number(m[1]) * 10000 + Number(m[2]) * 100 + (Number(m[3]) || 0)
  // TR Ay (Mayis/Mayıs/Mayis 2025/2025 Mayis)
  const lc = s.toLocaleLowerCase('tr-TR')
  let yearMatch = s.match(/(19|20)\d{2}/)
  let year = yearMatch ? Number(yearMatch[0]) : 0
  for (const ay in TR_AY_MAP) {
    if (lc.includes(ay)) return year * 100 + TR_AY_MAP[ay]
  }
  // Generic Date.parse
  const ts = Date.parse(s)
  if (!isNaN(ts)) return ts
  return null
}
// X ekseni karsilastirma fonksiyonu — once xSortKey, sonra localeCompare
const xCompare = (a, b) => {
  const ak = xSortKey(a), bk = xSortKey(b)
  if (ak !== null && bk !== null) return ak - bk
  if (ak !== null) return -1
  if (bk !== null) return 1
  return String(a || '').localeCompare(String(b || ''))
}

const formatXLabel = (v) => {
  if (v == null) return ''
  const s = String(v)
  // Sadece sayi ise ve 1-12 arasindaysa ay adi
  const n = Number(v)
  if (Number.isInteger(n) && n >= 1 && n <= 12) return AY_ADLARI[n] || s
  // 2026-03 formatindaysa
  const m = s.match(/^\d{4}-(\d{2})$/)
  if (m) return AY_ADLARI[parseInt(m[1])] + ' ' + s.substring(0,4)
  // ISO tarih: 2025-07-07 veya 2025-07-07T00:00:00 -> 07.07.2025 (saat kismini at)
  const d = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (d) return `${d[3]}.${d[2]}.${d[1]}`
  return truncateLabel(s, 20)
}

// Tarih hucrelerinde anlamsiz gece yarisi saatini (00:00 / 00.00) gizle — gercek saatler kalir
// "2026-07-04T00:00:00" -> "2026-07-04", "04.07.2026 00.00" -> "04.07.2026", "04.07.2026 14.30" -> aynen
const stripMidnight = (s) => {
  if (typeof s !== 'string') return s
  return s.replace(/[T ]00[:.]00(:00)?(\.\d+)?$/, '')
}

// Sayi formatlama — sirket ayarlarina gore
const getNumberFormat = () => {
  try {
    const selectedCid = localStorage.getItem('selectedCompanyId')
    const fmt = JSON.parse(localStorage.getItem('numberFormat') || 'null')
    return fmt || {}
  } catch { return {} }
}

// Gorunum override (Milyon/Bin/Tum) - kullanici sag ust panelden secer
const getNumberView = () => {
  try { return localStorage.getItem('numberView') || 'auto' } catch { return 'auto' }
}

const formatNumber = (v, noDec) => {
  if (v == null) return ''
  if (typeof v !== 'number') return v

  // view: 'short' (otomatik kisaltma) veya 'all' (tam sayi)
  // Eski degerler ('million','thousand','auto') 'short' olarak ele alinir
  let view = getNumberView()
  if (view !== 'all') view = 'short'

  const tSep = '.', dSep = ','
  let val = v, suffix = '', dec

  if (view === 'short') {
    // 6 basamaktan fazla (>= 1.000.000) -> M, az ise -> B (kucuk degerler kisaltilmaz)
    if (Math.abs(v) >= 1e6) {
      val = v / 1e6; suffix = ' M'; dec = 2
    } else if (Math.abs(v) >= 1e3) {
      val = v / 1e3; suffix = ' B'; dec = 2
    } else {
      // 1000 alti (fiyat vs.): oldugu gibi, kusurati koru
      dec = Number.isInteger(v) ? 0 : 2
    }
  } else {
    // 'all' - tam sayi: tam sayi degerler icin 0, ondalikli icin 2
    dec = Number.isInteger(v) ? 0 : 2
  }
  if (noDec) dec = 0  // lojistik: virgülden sonrası hiç gösterilmesin

  const parts = val.toFixed(dec).split('.')
  const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, tSep)
  return intPart + (dec > 0 ? dSep + parts[1] : '') + suffix
}

// Renk paletleri - kullanici sag ust panelden secer
const COLOR_PALETTES = {
  default: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'],
  vivid:   ['#dc2626', '#2563eb', '#16a34a', '#ca8a04', '#9333ea', '#0891b2', '#db2777', '#65a30d', '#ea580c', '#4f46e5'],
  pastel:  ['#93c5fd', '#86efac', '#fcd34d', '#fca5a5', '#c4b5fd', '#f9a8d4', '#7dd3fc', '#bef264', '#fdba74', '#a5b4fc'],
  mono:    ['#1e3a8a', '#1e40af', '#1d4ed8', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe', '#eff6ff'],
  warm:    ['#dc2626', '#ea580c', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#dc2626', '#f97316', '#fbbf24', '#a3e635'],
  cool:    ['#0ea5e9', '#06b6d4', '#14b8a6', '#10b981', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#22d3ee', '#2dd4bf'],
}

const getPalette = () => {
  try { return COLOR_PALETTES[localStorage.getItem('colorPalette')] || COLOR_PALETTES.default } catch { return COLOR_PALETTES.default }
}

// Isim -> sabit renk eslestirmesi (filter sonrasi rengi degismez)
const stableColorFor = (name) => {
  const s = String(name || '')
  let h = 0
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0
  const p = getPalette()
  return p[Math.abs(h) % p.length]
}

// Tablo boyut yardimcilari
const getTableSizes = () => {
  const col = (typeof localStorage !== 'undefined' && localStorage.getItem('tableColSize')) || 'md'
  const fnt = (typeof localStorage !== 'undefined' && localStorage.getItem('tableFontSize')) || 'md'
  const padX = col === 'sm' ? 'px-1.5' : col === 'lg' ? 'px-4' : 'px-2'
  const padY = col === 'sm' ? 'py-0.5' : col === 'lg' ? 'py-2' : 'py-1'
  const font = fnt === 'sm' ? 'text-[10px]' : fnt === 'lg' ? 'text-sm' : 'text-xs'
  // Slider ile px bazlı font (varsa) — yoksa küçük/orta/büyük eşlemesi
  const _px = Number(typeof localStorage !== 'undefined' && localStorage.getItem('tableFontSizePx'))
  const fontPx = (_px && _px >= 9 && _px <= 28) ? _px : (fnt === 'sm' ? 12 : fnt === 'lg' ? 18 : 15)
  return { padX, padY, font, fontPx, col, fnt }
}

// Sutun adlarini Turkce/temiz yaz: BU_YIL_MIKTAR -> Bu Yil Miktar
const TR_WORDS = {
  GECEN: 'Geçen', YIL: 'Yıl', BU: 'Bu', AY: 'Ay', GUN: 'Gün', HAFTA: 'Hafta', SAAT: 'Saat',
  TUTAR: 'Tutar', MIKTAR: 'Miktar', TOPLAM: 'Toplam', DEGER: 'Değer', ORAN: 'Oran',
  URUN: 'Ürün', TARIH: 'Tarih', LITRE: 'Litre', ADET: 'Adet', FIYAT: 'Fiyat',
  NAKIT: 'Nakit', TAHSILAT: 'Tahsilat', KART: 'Kart', POS: 'POS', POSGELIR: 'POS',
  BUGUN: 'Bugün', DUN: 'Dün',
  BORC: 'Borç', ALACAK: 'Alacak', BAKIYE: 'Bakiye',
  UNVAN: 'Unvan', STOKADI: 'Stok Adı', STOKADI1: 'Stok Adı', BLCARI: 'Cari', CARI: 'Cari',
  ISLEM: 'İşlem', FATURA: 'Fatura',
  BENZIN: 'Benzin', MOTORIN: 'Motorin', LPG: 'LPG', YAKIT: 'Yakıt',
  GIREN: 'Giren', CIKAN: 'Çıkan', SATIS: 'Satış', ALIS: 'Alış', KAR: 'Kâr',
  AMOUNT: 'Miktar', TOTAL: 'Tutar', RTIME: 'Tarih',
  SON: 'Son', ILK: 'İlk', ORTALAMA: 'Ortalama',
  ETKILE: 'Etkile', IPTAL: 'İptal',
}
const beautifyName = (s) => {
  if (s == null || typeof s !== 'string') return s
  if (!/[_A-Z]/.test(s) || s.length < 2) return s  // Zaten guzel formatta
  return s.split(/[_\s]+/).filter(Boolean).map(w => {
    const u = w.toUpperCase()
    if (TR_WORDS[u]) return TR_WORDS[u]
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
  }).join(' ')
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  // Scatter/bubble icin: label yoksa data point'in STOKADI/URUN/labels alanini bul
  const dp = payload[0]?.payload || {}
  let fallbackLabel = label || dp.STOKADI || dp.URUN || dp.GRUP || dp.PLAKA || ''
  // "2025-01" -> "2025 Ocak"
  const _aym = String(fallbackLabel).match(/^(\d{4})-(\d{2})$/)
  if (_aym) fallbackLabel = `${_aym[1]} ${AY_ADLARI[parseInt(_aym[2])] || _aym[2]}`
  return (
    <div className="bg-white dark:bg-gray-800 dark:text-gray-100 rounded-lg shadow-lg border dark:border-gray-700 p-2 text-xs max-w-xs">
      {fallbackLabel && <p className="font-medium text-gray-700 dark:text-gray-200 mb-1">{fallbackLabel}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {beautifyName(p.name)}: {formatNumber(p.value)}
        </p>
      ))}
    </div>
  )
}

// ===== Tank doluluk görünümü (maildeki gibi): üstte yakıt tablosu + toplu çubuk + tek tek dikey iç içe çubuk =====
function _tankFmt(v) { try { return Math.round(Number(v) || 0).toLocaleString('tr-TR') } catch { return v } }
function TankBar({ t }) {
  const dol = Math.max(0, Math.min(100, t.doluluk || 0))
  const low = (t.doluluk || 0) < 20, mid = !low && (t.doluluk || 0) < 40
  const col = low ? '#dc2626' : (mid ? '#f59e0b' : '#2563eb')
  // Modern: dikey gradyan dolgu, yuvarlak köşeler, ince gölge
  const grad = low ? 'linear-gradient(180deg,#f87171 0%,#dc2626 100%)'
            : mid ? 'linear-gradient(180deg,#fbbf24 0%,#f59e0b 100%)'
                  : 'linear-gradient(180deg,#60a5fa 0%,#2563eb 100%)'
  return (
    <div className="flex flex-col items-center flex-shrink-0" style={{ width: 66 }}>
      <div className="text-[10px] text-gray-400 dark:text-gray-500 tabular-nums mb-0.5">{_tankFmt(t.kapasite)}</div>
      <div className="relative rounded-xl overflow-hidden flex flex-col justify-end shadow-inner"
           style={{ width: 50, height: 150, background: 'linear-gradient(180deg,#f1f5f9 0%,#e2e8f0 100%)' }}>
        <div className="absolute top-0 left-0 right-0 text-center text-[8px] text-gray-400 pt-1 tabular-nums">{_tankFmt(t.bos)}</div>
        <div className="w-full flex items-end justify-center transition-all duration-500"
             style={{ height: `${dol}%`, background: grad, borderRadius: dol >= 99 ? '12px' : '8px 8px 0 0', boxShadow: '0 -1px 4px rgba(0,0,0,0.12)' }}>
          <span className="text-[9px] text-white font-medium pb-1 tabular-nums leading-none drop-shadow">{_tankFmt(t.seviye)}</span>
        </div>
      </div>
      <div className="text-[11px] font-bold tabular-nums mt-1 px-1.5 py-0.5 rounded-full"
           style={{ color: '#fff', background: col }}>%{(t.doluluk || 0).toFixed(0)}</div>
      <div className="text-[8px] text-gray-500 dark:text-gray-400 text-center leading-tight mt-0.5">{t.label1}{t.label2 ? <><br />{t.label2}</> : null}</div>
    </div>
  )
}
export function TankView({ data }) {
  const YMAP = { Mtrn: 'Motorin', K95: 'Benzin', LPG: 'LPG' }
  const YSIRA = { Motorin: 0, Benzin: 1, LPG: 2 }
  const ist = {}
  ;(data || []).forEach(r => {
    const s = String(r.Istasyon_Adi || r.istasyon || '?').trim()
    if (!ist[s]) ist[s] = []
    const sev = Number(r.value ?? r.Seviye ?? r.seviye) || 0
    const kap = Number(r.Kapasite ?? r.kapasite) || 0
    ist[s].push({
      tank: String(r.TankNo ?? r.tank ?? '?'),
      yakit: YMAP[r.UrunAd] || r.UrunAd || r.Yakit || r.yakit || '?',
      seviye: sev, kapasite: kap, bos: Math.max(kap - sev, 0),
      doluluk: Number(r.Doluluk ?? r.doluluk) || (kap > 0 ? sev / kap * 100 : 0),
    })
  })
  const TH = "border border-gray-200 dark:border-gray-600 px-1.5 py-1 bg-blue-50 dark:bg-gray-800 text-left text-gray-600 dark:text-gray-300 font-semibold"
  const TD = "border border-gray-200 dark:border-gray-600 px-1.5 py-1 text-gray-700 dark:text-gray-200"
  return (
    <div className="p-2 grid grid-cols-1 lg:grid-cols-3 gap-3 items-start">
      {Object.entries(ist).map(([istAd, tanks]) => {
        const yg = {}
        tanks.forEach(t => { if (!yg[t.yakit]) yg[t.yakit] = { yakit: t.yakit, seviye: 0, kapasite: 0 }; yg[t.yakit].seviye += t.seviye; yg[t.yakit].kapasite += t.kapasite })
        const yakitlar = Object.values(yg).sort((a, b) => (YSIRA[a.yakit] ?? 9) - (YSIRA[b.yakit] ?? 9))
        const topSev = yakitlar.reduce((s, y) => s + y.seviye, 0), topKap = yakitlar.reduce((s, y) => s + y.kapasite, 0)
        const tanksSorted = [...tanks].sort((a, b) => (YSIRA[a.yakit] ?? 9) - (YSIRA[b.yakit] ?? 9) || String(a.tank).localeCompare(String(b.tank)))
        const yakitBars = yakitlar.map(y => ({ label1: y.yakit, seviye: y.seviye, kapasite: y.kapasite, bos: Math.max(y.kapasite - y.seviye, 0), doluluk: y.kapasite > 0 ? y.seviye / y.kapasite * 100 : 0 }))
        return (
          <div key={istAd} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 border border-gray-100 dark:border-gray-700">
            <h4 className="text-sm font-semibold text-blue-700 dark:text-blue-400 mb-2">{istAd}</h4>
            <table className="w-full text-[10px] border-collapse mb-3 rounded-lg overflow-hidden"><thead><tr>{['Yakıt', 'Seviye', 'Boş', 'Kapasite'].map(h => <th key={h} className={TH}>{h}</th>)}</tr></thead>
              <tbody>
                {yakitlar.map(y => <tr key={y.yakit}><td className={TD}>{y.yakit}</td><td className={`${TD} text-right tabular-nums`}>{_tankFmt(y.seviye)}</td><td className={`${TD} text-right tabular-nums`}>{_tankFmt(y.kapasite - y.seviye)}</td><td className={`${TD} text-right tabular-nums`}>{_tankFmt(y.kapasite)}</td></tr>)}
                <tr className="font-bold"><td className={TD}>TOPLAM</td><td className={`${TD} text-right tabular-nums`}>{_tankFmt(topSev)}</td><td className={`${TD} text-right tabular-nums`}>{_tankFmt(topKap - topSev)}</td><td className={`${TD} text-right tabular-nums`}>{_tankFmt(topKap)}</td></tr>
              </tbody>
            </table>
            <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-1 font-medium">Yakıt bazlı toplam</div>
            <div className="flex items-end gap-2 mb-3 flex-wrap">{yakitBars.map((b, i) => <TankBar key={i} t={b} />)}</div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-1 font-medium">Tanklar</div>
            <div className="flex items-end gap-2 flex-wrap">{tanksSorted.map((t, i) => <TankBar key={i} t={{ ...t, label1: t.tank, label2: t.yakit }} />)}</div>
          </div>
        )
      })}
    </div>
  )
}

export default function ChartWidget({ type, data, stations, config, title, widgetId, dashId, dateFrom, dateTo, showMode, numberView, colorPalette, theme, tableColSize, tableFontSize, onCrossFilter, onDrillDown, pocanSube, aracTipi, aracTuru, currencyMode }) {
  // Lojistik dashboard'lari: cari tiklamalari filtre DEGIL detay modali acar
  const isLojDash = dashId === 11 || dashId === 14
  const isPetrolDash = dashId === 8
  const [lojPieCari, setLojPieCari] = useState(null)
  const [lojGiderDetay, setLojGiderDetay] = useState(null)  // Gider Raporu pastası -> tüm giderler hiyerarşik modal
  const [lojAy, setLojAy] = useState(null)
  const [petrolTreeCari, setPetrolTreeCari] = useState(null)  // treemap cari tiklamasi (petrol)
  const [petrolPieAlis, setPetrolPieAlis] = useState(null)  // pie urun tiklamasi -> son alislar (petrol)
  const [lojTreeArac, setLojTreeArac] = useState(null)  // lojistik treemap plaka -> arac modal
  const [lojTreeCari, setLojTreeCari] = useState(null)  // lojistik treemap cari -> cari modal
  const [lojSofor, setLojSofor] = useState(null)  // lojistik bar sofor -> sofor modal
  const [lojSeferListe, setLojSeferListe] = useState(null)  // Aylık Kar Analizi: gün/şehir tıkla -> sefer listesi modal
  const [lojKart, setLojKart] = useState(null)  // Genel Özet KPI kartına tıkla -> ilgili detay modal
  const [selectedDoviz, setSelectedDoviz] = useState('€')  // Aylık Alacak ve Kar: döviz seçici (€/₺/$)
  const lojTt = dashId === 14 ? 'YD' : 'YI'
  // NOT: erken return'ler HOOK'lardan SONRA olmali (React kurali) — asagida, useMemo'dan sonra.

  const xCol = config?.x_column
  let yCols = config?.y_columns || []
  const labelCol = config?.label_column
  let valueCol = config?.value_column

  // Miktar/Tutar switch (lock_columns flag varsa atla — yuzdelik grafikler vb.)
  if (showMode && data?.length > 0 && !config?.lock_columns) {
    const keys = Object.keys(data[0])
    const miktarKeys = ['AMOUNT','MIKTAR','LITRE','ADET','GIREN_MIKTAR','CIKAN_MIKTAR']
    const tutarKeys = ['TOTAL','TUTAR','TOPLAM','DEGER','GIREN_TOPLAM','CIKAN_TOPLAM']

    if (showMode === 'miktar') {
      // _MIKTAR suffix'li kolonlar (BU_YIL_MIKTAR vs)
      const suffixed = yCols.map(c => c + '_MIKTAR').filter(c => keys.includes(c))
      if (suffixed.length > 0) {
        yCols = suffixed
      } else {
        const mc = keys.filter(k => miktarKeys.includes(k.toUpperCase()))
        if (mc.length > 0) { yCols = mc; if (valueCol) valueCol = mc[0] }
      }
    } else {
      // Tutar modunda miktar kolonlarini cikar (hem tam esit hem _MIKTAR suffix'li)
      const isMiktar = (c) => {
        const u = c.toUpperCase()
        return miktarKeys.includes(u) || u.endsWith('_MIKTAR') || u.endsWith('MIKTAR')
      }
      const onlyTutar = yCols.filter(c => !isMiktar(c))
      if (onlyTutar.length > 0) {
        yCols = onlyTutar
      } else {
        // y_columns tamamen miktar — keys'ten tutar kolonu bulup gec
        const tc = keys.filter(k => tutarKeys.includes(k.toUpperCase()))
        if (tc.length > 0) { yCols = tc; if (valueCol) valueCol = tc[0] }
      }
    }
  }

  // X kolonuna gore grupla — ayni x degeri birden fazla satırda varsa topla
  const chartData = useMemo(() => {
    // Döviz seçici (Aylık Alacak ve Kar): grafik ay bazında grupladığı için filtre burada, gruplama ÖNCESİ
    const data0 = (config?.doviz_secici && selectedDoviz && Array.isArray(data))
      ? data.filter(r => (r.DOVIZ ?? r.doviz) === selectedDoviz) : data
    if (!xCol || !data0?.length || data0.length < 2) return data0
    // color_column varsa pivot: her color degeri yeni bir kolon olur
    const colorCol = config?.color_column
    if (colorCol && yCols.length === 1) {
      const yc = yCols[0]
      const grouped = {}
      data0.forEach(row => {
        const xKey = String(row[xCol] ?? '')
        const colorVal = String(row[colorCol] ?? '')
        if (!grouped[xKey]) grouped[xKey] = { [xCol]: row[xCol] }
        grouped[xKey][colorVal] = (grouped[xKey][colorVal] || 0) + (Number(row[yc]) || 0)
      })
      return Object.values(grouped).sort((a, b) => xCompare(a[xCol], b[xCol]))
    }
    const grouped = {}
    data0.forEach(row => {
      const key = String(row[xCol] || '')
      if (!grouped[key]) grouped[key] = { ...row }
      else {
        yCols.forEach(yc => { grouped[key][yc] = (grouped[key][yc] || 0) + (Number(row[yc]) || 0) })
      }
    })
    let result = Object.values(grouped)
    if (result.length >= data0.length) result = data0
    // Line/area/bar chart: x ekseni akilli sirala (numeric, ISO tarih, TR ay, generic date)
    if (['line','area','multi_line','composed','bar'].includes(type)) {
      const xName = String(xCol || '').toUpperCase()
      if (xName.includes('TARIH') || xName.includes('DATE') || xName.includes('AY') || xName === 'GUN' || xName === 'AY_NO') {
        result = [...result].sort((a, b) => xCompare(a[xCol], b[xCol]))
      }
    }
    // Horizontal bar: showMode'a gore degerlere göre desc sirala (En Cok Satilan vb.)
    if (type === 'horizontal_bar' && yCols.length > 0) {
      const sortCol = yCols[0]  // showMode'a göre zaten TUTAR veya MIKTAR olarak set edildi
      if (config?.x_chrono) {
        // config.x_chrono: degere gore DEGIL kronolojik sirala (Son 3 Ay -> Mayis, Haziran, Temmuz).
        // "En Cok" tipi yatay barlar bu bayragi ALMAZ -> degere gore sirali kalir.
        result = [...result].sort((a, b) => xCompare(a[xCol], b[xCol]))
      } else if (config?.color_column && result.length > 0) {
        const xToplam = {}
        for (const row of result) {
          const xv = row[xCol]
          xToplam[xv] = (xToplam[xv] || 0) + (Number(row[sortCol]) || 0)
        }
        // x degerlerini topluma göre desc sirala
        const xSirali = Object.keys(xToplam).sort((a, b) => xToplam[b] - xToplam[a])
        // Sirayla x'leri al, sonrasinda ayni x icin row'lari ekle
        const newResult = []
        for (const xv of xSirali) {
          for (const row of result) {
            if (String(row[xCol]) === String(xv)) newResult.push(row)
          }
        }
        result = newResult
      } else {
        result = [...result].sort((a, b) => (Number(b[sortCol]) || 0) - (Number(a[sortCol]) || 0))
      }
    }
    // display_limit varsa kisalt
    const limit = config?.display_limit
    if (limit && result.length > limit) result = result.slice(0, limit)
    return result
  }, [data, xCol, yCols, config?.display_limit, config?.color_column, type, showMode, config?.doviz_secici, selectedDoviz])

  // Erken cikislar — TUM hook'lardan sonra (React #310 fix)
  // Tank doluluk widget'i (detay veri) → maildeki düzen
  if (config?._tank_data && !config?._tank_summary) {
    return <TankView data={data} />
  }
  const _hasStations = Array.isArray(stations) && stations.length > 0
  if ((!data || data.length === 0) && !_hasStations) {
    return <div className="flex items-center justify-center h-full text-gray-400 text-sm">Veri yok</div>
  }

  const handleChartClick = (item) => {
    if (!item) return
    const payload = item.activePayload?.[0]?.payload || item.payload || item
    // Lojistik Sürücü Sefer grafiği: şoföre tıkla -> şoför işlem/sefer modalı (cross-filter yerine)
    if (isLojDash && (payload.SOFOR ?? payload.sofor)) {
      setLojSofor(String(payload.SOFOR ?? payload.sofor)); return
    }
    // Aylık Kar Analizi "Önceki Aylarla Karşılaştırma" (X=GUN, renk=AY): güne tıkla -> gün filtresi DEĞİL,
    // o günün son 3 aydaki (bu ay/geçen ay/önceki ay) sefer listesi modalı
    if (isLojDash && String(xCol).toUpperCase() === 'GUN' && config?.color_column) {
      const g = payload.GUN ?? payload.gun ?? payload[xCol]
      if (g != null) { setLojSeferListe({ mod: 'gun', deger: String(g) }); return }
    }
    // Lojistik grafik: PLAKA'ya tıkla -> o aracın SEFER listesi (gelir/gider ile), cross-filter DEĞİL
    if (isLojDash && String(xCol).toUpperCase() === 'PLAKA') {
      const p = payload.PLAKA ?? payload.plaka ?? payload[xCol]
      if (typeof p === 'string' && p.trim() && p.trim() !== '-') { setLojSeferListe({ mod: 'plaka', deger: p.trim() }); return }
    }
    // Lojistik aylık trend: AY'a (YYYY-MM) tıkla -> o ayın sefer listesi modalı
    if (isLojDash && String(xCol).toUpperCase() === 'AY') {
      const ay = payload.AY ?? payload.ay ?? payload[xCol]
      const m = String(ay ?? '').match(/^(\d{4})-(\d{2})/)
      if (m) {
        const bas = `${m[1]}-${m[2]}-01`
        const son = new Date(Number(m[1]), Number(m[2]), 0).getDate()
        const bit = `${m[1]}-${m[2]}-${String(son).padStart(2, '0')}`
        setLojSeferListe({ mod: 'tum', deger: '', bas, bit }); return
      }
    }
    if (!onCrossFilter) return
    if (xCol && payload[xCol] !== undefined) {
      onCrossFilter(xCol, payload[xCol])
    } else if (labelCol && payload[labelCol] !== undefined) {
      onCrossFilter(labelCol, payload[labelCol])
    }
  }

  switch (type) {
    case 'kpi': {
      // Genel Özet KPI kartına tıkla -> başlığa göre ilgili modal
      const kartTikla = () => {
        const T = String(title || '').toLowerCase()
        const _tr = config?.loj_arac_turu ?? aracTuru   // minivan sayfası: sabit '3'
        if (/minivan\s*say|ara[cç]\s*say/.test(T)) setLojKart({ type: 'aracliste', value: '', extra: { tt: lojTt, at: aracTipi, tr: _tr } })
        else if (/toplam\s*sefer|sefer\s*say/.test(T)) setLojKart({ type: 'seferliste', value: '', extra: { mod: 'tum', tt: lojTt, at: aracTipi, tr: _tr, bas: dateFrom, bit: dateTo } })
        else if (/toplam\s*gelir|toplam\s*gider/.test(T)) setLojKart({ type: 'seferliste', value: '', extra: { mod: 'tum', tt: lojTt, at: aracTipi, tr: _tr, bas: dateFrom, bit: dateTo } })
        else if (/toplam\s*alacak/.test(T)) setLojKart({ type: 'cariliste', value: 'alacakli', extra: { tt: lojTt, bas: dateFrom, bit: dateTo } })
        else if (/toplam\s*bor/.test(T)) setLojKart({ type: 'cariliste', value: 'borclu', extra: { tt: lojTt, bas: dateFrom, bit: dateTo } })
        else if (/bakiye/.test(T)) setLojKart({ type: 'cariliste', value: 'tum', extra: { tt: lojTt, bas: dateFrom, bit: dateTo } })
        else if (/[cç]ekici/.test(T)) setLojKart({ type: 'aracliste', value: '', extra: { tt: lojTt, at: aracTipi, tr: _tr } })
      }
      return (
        <>
        <KPICard data={data} config={config} title={title} showMode={showMode} noDec={isLojDash} currencyMode={currencyMode}
          onClick={isLojDash ? kartTikla : undefined} />
        {lojKart && <LojDetayModal type={lojKart.type} value={lojKart.value} extra={lojKart.extra} onClose={() => setLojKart(null)} />}
        </>
      )
    }

    case 'bar':
    case 'horizontal_bar': {
      // color_column varsa pivot edilmis kolonlardan bar ciz
      const colorCol = config?.color_column
      let barKeys = yCols
      if (colorCol && chartData.length && yCols.length === 1) {
        const distinctColors = [...new Set(data.map(r => String(r[colorCol] ?? '')).filter(Boolean))]
        if (distinctColors.length) barKeys = distinctColors
      }
      // Cok satir varsa Brush ekle (kaydırılabilir) — 15+ veri noktasi varsa
      const showBrush = chartData.length > 15
      const brushStart = 0
      const brushEnd = type === 'horizontal_bar' ? Math.min(14, chartData.length - 1) : Math.min(19, chartData.length - 1)
      return (
        <>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} layout={type === 'horizontal_bar' ? 'vertical' : 'horizontal'} onClick={handleChartClick} margin={{ bottom: showBrush ? 25 : 5, left: 5, right: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            {type === 'horizontal_bar' ? (
              <>
                <YAxis dataKey={xCol} type="category" width={140} tick={{ fontSize: 10 }} tickFormatter={(v) => truncateLabel(v, 22)} />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={formatNumber} />
              </>
            ) : (
              <>
                <XAxis dataKey={xCol} tick={{ fontSize: 9, angle: -45, textAnchor: 'end' }} height={48} interval={0}
                  tickFormatter={(v) => {
                    // Gun etiketleri kisa: 07.07 (tam tarih tooltip'te) — uzun etiket + 80px alan grafigi yutuyordu
                    const m = String(v ?? '').match(/^\d{4}-(\d{2})-(\d{2})/)
                    return m ? `${m[2]}.${m[1]}` : formatXLabel(v)
                  }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={formatNumber} width={80} />
              </>
            )}
            <Tooltip content={<CustomTooltip />} />
            {barKeys.length > 1 && <Legend formatter={(value) => beautifyName(value)} />}
            {barKeys.map((col, i) => (
              <Bar key={col} dataKey={col} fill={COLORS[i % COLORS.length]} radius={[4, 4, 0, 0]} cursor="pointer" />
            ))}
            {showBrush && (
              <Brush dataKey={xCol} height={22} stroke="#94a3b8" fill="#f1f5f9" travellerWidth={8}
                     startIndex={brushStart} endIndex={brushEnd} tickFormatter={(v) => truncateLabel(String(v||''), 12)} />
            )}
          </BarChart>
        </ResponsiveContainer>
        {lojSofor && <LojDetayModal type="sofor" value={lojSofor} onClose={() => setLojSofor(null)} />}
        {lojTreeArac && <LojDetayModal type="arac" value={lojTreeArac} extra={{ tum: true }} onClose={() => setLojTreeArac(null)} />}
        {lojSeferListe && <LojDetayModal type="seferliste" value={lojSeferListe.deger}
          extra={{ mod: lojSeferListe.mod, tt: lojTt, at: aracTipi, tr: config?.loj_arac_turu ?? aracTuru, bas: lojSeferListe.bas, bit: lojSeferListe.bit }} onClose={() => setLojSeferListe(null)} />}
        </>
      )
    }

    case 'map':
      return (
        <>
        <Suspense fallback={<div className="flex items-center justify-center h-full text-gray-400 text-xs">Harita yükleniyor...</div>}>
          <MapWidget data={data} config={config}
            onSelect={isLojDash ? (sehir) => sehir && setLojSeferListe({ mod: 'sehir', deger: String(sehir) }) : undefined} />
        </Suspense>
        {lojSeferListe && <LojDetayModal type="seferliste" value={lojSeferListe.deger}
          extra={{ mod: lojSeferListe.mod, tt: lojTt, at: aracTipi, tr: aracTuru }} onClose={() => setLojSeferListe(null)} />}
        </>
      )

    case 'composed': {
      const barKeys = config?.bar_columns || []
      const lineKeys = config?.line_columns || []
      const seriesColors = config?.series_colors || {}
      // overlay_bars: barlar yan yana degil ic ice — ilk bar genis (kap), sonrakiler dar (dolgu)
      const overlay = !!config?.overlay_bars && barKeys.length > 1
      // Döviz seçici (Aylık Alacak ve Kar): veri içindeki para birimleri (₺/€/$ sırasıyla)
      const _dovizler = config?.doviz_secici ? ['₺', '€', '$'].filter(dv => (data || []).some(r => (r.DOVIZ ?? r.doviz) === dv)) : []
      // Lojistik dashboard'da aya tiklaninca o ayin islemleri modalda acilir
      const composedClick = (item) => {
        const payload = item?.activePayload?.[0]?.payload
        const xVal = payload?.[xCol]
        if (isLojDash && /^\d{4}-\d{2}$/.test(String(xVal ?? ''))) { setLojAy(String(xVal)); return }
        handleChartClick(item)
      }
      return (
        <div className="flex flex-col h-full">
        {_dovizler.length > 0 && (
          <div className="flex gap-1 justify-end mb-1 shrink-0">
            {_dovizler.map(dv => (
              <button key={dv} onClick={() => setSelectedDoviz(dv)}
                className={`text-xs px-2.5 py-0.5 rounded font-semibold border transition-colors ${selectedDoviz === dv ? 'bg-primary-600 text-white border-primary-600' : 'bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                {dv}
              </button>
            ))}
          </div>
        )}
        <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} onClick={composedClick} style={isLojDash ? { cursor: 'pointer' } : undefined}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey={xCol} tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={60}
              tickFormatter={(v) => {
                const m = String(v ?? '').match(/^(\d{4})-(\d{2})$/)
                return m ? `${m[1]} ${AY_ADLARI[parseInt(m[2])] || m[2]}` : formatXLabel(v)
              }} />
            {overlay && <XAxis xAxisId="ov" dataKey={xCol} hide />}
            <YAxis tick={{ fontSize: 11 }} tickFormatter={formatNumber} />
            <Tooltip content={<CustomTooltip />} />
            <Legend formatter={(value) => beautifyName(value)} />
            {barKeys.map((col, i) => (
              <Bar key={col} dataKey={col} fill={seriesColors[col] || COLORS[i % COLORS.length]}
                xAxisId={overlay && i > 0 ? 'ov' : 0}
                barSize={overlay ? (i === 0 ? 22 : 11) : undefined}
                fillOpacity={overlay && i === 0 ? 0.45 : 1}
                cursor={isLojDash ? 'pointer' : undefined} />
            ))}
            {lineKeys.map((col, i) => (
              <Line key={col} type="monotone" dataKey={col} stroke={seriesColors[col] || COLORS[(barKeys.length + i) % COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
        </div>
        {lojAy && <LojDetayModal type="ay" value={lojAy} extra={{ tip: dashId === 14 ? 2 : 1 }} onClose={() => setLojAy(null)} />}
        </div>
      )
    }

    case 'line':
    case 'multi_line': {
      // color_column varsa pivot edilmis kolonlardan line ciz
      const colorCol = config?.color_column
      let lineKeys = yCols
      if (colorCol && chartData.length && yCols.length === 1) {
        const distinctColors = [...new Set(data.map(r => String(r[colorCol] ?? '')).filter(Boolean))]
        lineKeys = distinctColors
      }
      return (
        <>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} onClick={handleChartClick} style={isLojDash && config?.color_column ? { cursor: 'pointer' } : undefined}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey={xCol} tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={formatNumber} />
            <Tooltip content={<CustomTooltip />} />
            {lineKeys.length > 1 && <Legend formatter={(value) => beautifyName(value)} />}
            {lineKeys.map((col, i) => (
              <Line key={col} type="monotone" dataKey={col} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls />
            ))}
          </LineChart>
        </ResponsiveContainer>
        {lojSeferListe && <LojDetayModal type="seferliste" value={lojSeferListe.deger}
          extra={{ mod: lojSeferListe.mod, tt: lojTt, at: aracTipi, tr: aracTuru }} onClose={() => setLojSeferListe(null)} />}
        </>
      )
    }

    case 'area': {
      // color_column varsa pivot edilmis kolonlardan area ciz
      const colorColArea = config?.color_column
      let areaKeys = yCols
      if (colorColArea && chartData.length && yCols.length === 1) {
        const distinctColors = [...new Set(data.map(r => String(r[colorColArea] ?? '')).filter(Boolean))]
        if (distinctColors.length) areaKeys = distinctColors
      }
      return (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} onClick={handleChartClick}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey={xCol} tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={formatNumber} />
            <Tooltip content={<CustomTooltip />} />
            {areaKeys.length > 1 && <Legend formatter={(value) => beautifyName(value)} />}
            {areaKeys.map((col, i) => (
              <Area key={col} type="monotone" dataKey={col} fill={COLORS[i % COLORS.length]} fillOpacity={0.15} stroke={COLORS[i % COLORS.length]} strokeWidth={2} connectNulls />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      )
    }

    case 'pie':
    case 'donut': {
      // Label'a gore grupla (ayni label birden fazla satırda olabilir)
      const pieKey = valueCol || yCols[0]
      const pieLabelKey = labelCol || xCol
      const pieGrouped = {}
      data.forEach(r => {
        const lbl = String(r[pieLabelKey] || '').trim()
        if (!pieGrouped[lbl]) pieGrouped[lbl] = { ...r, [pieLabelKey]: lbl, [pieKey]: 0 }
        pieGrouped[lbl][pieKey] += (Number(r[pieKey]) || 0)
      })
      let pieData = Object.values(pieGrouped)
      // Negatif degerli pie chart cizilemez - mutlak deger kullan, orijinal degeri _origValue'da sakla
      pieData = pieData.map(p => ({ ...p, _origValue: Number(p[pieKey]) || 0, [pieKey]: Math.abs(Number(p[pieKey]) || 0) }))
      // Hepsi gelsin ama sirali olsun (label sadece ilk 10'da gosterilecek)
      pieData = pieData.sort((a, b) => (Number(b[pieKey]) || 0) - (Number(a[pieKey]) || 0))
      const PIE_LABEL_LIMIT = 10
      // Lojistik dash + cari bazli pasta: tiklama filtre yerine cari detay MODALI acar
      const pieTikla = (index) => {
        // Gider Raporu: hangi dilime tıklanırsa tıklansın TÜM giderler hiyerarşik modalda açılır
        // (küçük paylar tıklanamayabilir -> tek tıkla hepsi gelir)
        if (config?.gider_detay_modal) { setLojGiderDetay(true); return }
        const key = labelCol || xCol
        const item = pieData[index]
        if (!key || !item) return
        const val = item[key]
        if (isLojDash && typeof val === 'string' && val.trim() && ['CARI', 'UNVAN', 'MUSTERI'].includes(String(key).toUpperCase())) {
          setLojPieCari(val.trim()); return
        }
        // Petrol: urun dilimine tiklaninca son alislar modali (Urunlere Gore Kar vb.)
        if (isPetrolDash && typeof val === 'string' && val.trim() && ['STOKADI', 'URUN'].includes(String(key).toUpperCase())) {
          setPetrolPieAlis(val.trim()); return
        }
        if (onCrossFilter) onCrossFilter(key, val)
      }

      return (
        <>
        {lojPieCari && <LojDetayModal type="cari" value={lojPieCari} onClose={() => setLojPieCari(null)} />}
        {lojGiderDetay && <LojDetayModal type="giderdetay" extra={{ tt: lojTt, bas: dateFrom, bit: dateTo }} onClose={() => setLojGiderDetay(null)} />}
        {petrolPieAlis && <LojDetayModal type="petrol-alis" value={petrolPieAlis} onClose={() => setPetrolPieAlis(null)} />}
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieData}
              dataKey={pieKey}
              nameKey={labelCol || xCol}
              cx="50%" cy="50%"
              innerRadius={type === 'donut' ? '40%' : 0}
              outerRadius="75%"
              label={(props) => {
                // Etiket de TIKLANABILIR — kullanicilar dilim yerine yaziya basiyor
                const { name, percent, index, x, y, textAnchor } = props
                if (index >= PIE_LABEL_LIMIT) return null
                return (
                  <text x={x} y={y} textAnchor={textAnchor} fontSize={11} fill="#64748b"
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => { e.stopPropagation(); pieTikla(index) }}>
                    {`${name} ${(percent * 100).toFixed(0)}%`}
                  </text>
                )
              }}
              labelLine={false}
              onClick={(_, index) => pieTikla(index)}
              cursor="pointer"
            >
              {pieData.map((entry, i) => (
                <Cell key={i} fill={stableColorFor(entry[labelCol || xCol])} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        </>
      )
    }

    case 'treemap': {
      const tmKey = valueCol || yCols[0] || 'TUTAR'
      const tmLabelKey = labelCol || xCol || 'UNVAN'
      const tmGrouped = {}
      data.forEach(r => {
        const lbl = String(r[tmLabelKey] || '').trim()
        if (!lbl) return
        if (!tmGrouped[lbl]) tmGrouped[lbl] = { name: lbl, size: 0 }
        tmGrouped[lbl].size += (Number(r[tmKey]) || 0)
      })
      const tmData = Object.values(tmGrouped).sort((a, b) => b.size - a.size).slice(0, 50)

      return (
        <>
        <ResponsiveContainer width="100%" height="100%">
          <Treemap data={tmData} dataKey="size" aspectRatio={4/3}
            content={({ x, y, width, height, name, value, index }) => {
              if (!width || !height || width < 2 || height < 2) return null
              const color = COLORS[(index || 0) % COLORS.length]
              return (
                <g onClick={
                     isPetrolDash && name ? () => setPetrolTreeCari(String(name))
                     : isLojDash && name ? () => (/PLAKA|CEKICI|ÇEKICI|ARAC|ARAÇ/i.test(String(tmLabelKey)) ? setLojTreeArac(String(name)) : setLojTreeCari(String(name)))
                     : undefined}
                   style={(isPetrolDash || isLojDash) ? { cursor: 'pointer' } : undefined}>
                  <rect x={x} y={y} width={width} height={height} fill={color} fillOpacity={0.85} stroke="#fff" strokeWidth={1} rx={2} />
                  {width > 40 && height > 25 && <text x={x+3} y={y+13} fill="#fff" fontSize={9} fontWeight="bold">{String(name||'').substring(0, Math.floor(width/5.5))}</text>}
                  {width > 50 && height > 38 && <text x={x+3} y={y+25} fill="#fff" fontSize={8} fillOpacity={0.7}>{formatNumber(value, isLojDash)}</text>}
                </g>
              )
            }}>
            <Tooltip content={({ active, payload }) => {
              if (!active || !payload?.[0]) return null
              const p = payload[0].payload || {}
              return (
                <div className="bg-white dark:bg-gray-800 border rounded shadow-lg p-2 text-xs">
                  <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">{p.name}</div>
                  <div className="text-gray-700 dark:text-gray-300">{formatNumber(p.size, isLojDash)}</div>
                </div>
              )
            }} />
          </Treemap>
        </ResponsiveContainer>
        {petrolTreeCari && <LojDetayModal type="petrol-satis" value={petrolTreeCari} onClose={() => setPetrolTreeCari(null)} />}
        {lojTreeArac && <LojDetayModal type="arac" value={lojTreeArac} extra={{ tum: true }} onClose={() => setLojTreeArac(null)} />}
        {lojTreeCari && <LojDetayModal type="cari" value={lojTreeCari} onClose={() => setLojTreeCari(null)} />}
        </>
      )
    }

    case 'scatter': {
      const sxCol = config?.x_column
      const syCol = config?.y_column || (config?.y_columns && config.y_columns[0])
      if (!sxCol || !syCol) return <div className="flex items-center justify-center h-full text-gray-400 text-xs">Veri yok</div>
      // Bubble size kolonu: config.size_column veya MIKTAR fallback
      const sizeCol = config?.size_column || (data[0] && 'MIKTAR' in data[0] ? 'MIKTAR' : null)
      // Etiket kolonu - her noktanin yaninda yazilacak
      const scatterLabel = config?.label_column
      // Etiketleme stratejisi:
      // - 15'ten az nokta -> hepsine etiket
      // - 15+ nokta -> hicbirine etiket (sadece hover'da tooltip ile gozukur)
      // - config.show_labels=true ile zorla goster, config.label_top_n ile sadece top N
      const ETIKET_SINIRI = 15
      const forceShow = config?.show_labels === true
      const topN = config?.label_top_n
      const labelSet = new Set()
      if (scatterLabel && topN && data.length > topN && sizeCol) {
        const sorted = [...data].sort((a, b) => (Number(b[sizeCol]) || 0) - (Number(a[sizeCol]) || 0))
        for (let i = 0; i < topN && i < sorted.length; i++) {
          labelSet.add(String(sorted[i][scatterLabel]))
        }
      }
      const showLabel = (val) => {
        if (topN) return labelSet.has(String(val)) ? val : ''
        if (forceShow) return val
        // Default: az nokta -> goster, cok nokta -> gizle
        return (data.length <= ETIKET_SINIRI) ? val : ''
      }
      return (
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 10, right: 30, bottom: 30, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis type="number" dataKey={sxCol} name={sxCol} tick={{ fontSize: 11 }} tickFormatter={formatNumber}/>
            <YAxis type="number" dataKey={syCol} name={syCol} tick={{ fontSize: 11 }} tickFormatter={formatNumber}/>
            {sizeCol && <ZAxis type="number" dataKey={sizeCol} range={[80, 800]} name={sizeCol}/>}
            <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />
            <Scatter data={data} fill="#3b82f6" fillOpacity={0.65}>
              {data.map((entry, idx) => (
                <Cell key={`c-${idx}`} fill={COLORS[idx % 10]} />
              ))}
              {scatterLabel && (
                <LabelList dataKey={scatterLabel} position="top"
                  formatter={showLabel}
                  style={{ fontSize: 10, fontWeight: 600, fill: '#374151' }}/>
              )}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      )
    }

    case 'table':
      // Bakiye tablosu ise ozel BakiyeWidget kullan (PETROL/MARKET icin — Firebird /bakiye-full).
      // LOJISTIK HARIC (id 276-499 VEYA dashboard 11/14): onlarin verisi PG'den batch-data ile
      // gelir; BakiyeWidget'a sapmak Altinekin carilerini gosteriyordu (yanlis kaynak!).
      if (!((widgetId >= 276 && widgetId <= 499) || isLojDash) &&
          title && (title.toLowerCase().includes('bakiye') || title.toLowerCase().includes('cari bakiye'))) {
        return <BakiyeWidget datasourceId={config?.datasource_id || 6} sube={pocanSube || null} />
      }
      // Petrol pivot "Tüm" modu: her istasyon için ayrı tablo (alt alta)
      if (Array.isArray(stations) && stations.length > 0) {
        return (
          <div className="flex flex-col gap-4 overflow-auto h-full">
            {stations.map((st, i) => (
              <div key={i}>
                <div className="text-xs font-bold text-blue-700 dark:text-blue-400 mb-1 px-1 sticky left-0">🏢 {st.name}</div>
                <DataTable data={st.data} config={config} widgetId={widgetId} dashId={dashId} onCrossFilter={onCrossFilter} onDrillDown={onDrillDown} title={title} dateFrom={dateFrom} dateTo={dateTo} showMode={showMode} />
              </div>
            ))}
          </div>
        )
      }
      return <DataTable data={data} config={config} widgetId={widgetId} dashId={dashId} onCrossFilter={onCrossFilter} onDrillDown={onDrillDown} title={title} dateFrom={dateFrom} dateTo={dateTo} showMode={showMode} />

    case 'gauge':
      return <GaugeWidget data={data} config={config} showMode={showMode} widgetId={widgetId} />

    default:
      return <div className="flex items-center justify-center h-full text-gray-400 text-sm">Desteklenmeyen: {type}</div>
  }
}


function KPICard({ data, config, title, showMode, onClick, noDec, currencyMode }) {
  let valueCol = config?.value_column || config?.y_columns?.[0]

  // Miktar/Tutar switch (lock_columns flag varsa atla)
  if (showMode && data?.length > 0 && !config?.lock_columns) {
    const keys = Object.keys(data[0])
    const miktarCols = keys.filter(k => ['AMOUNT','MIKTAR','LITRE','ADET','GIREN_MIKTAR','CIKAN_MIKTAR'].includes(k.toUpperCase()))
    const tutarCols = keys.filter(k => ['TOTAL','TUTAR','TOPLAM','DEGER'].includes(k.toUpperCase()))
    if (showMode === 'miktar' && miktarCols.length > 0) valueCol = miktarCols[0]
    else if (showMode === 'tutar' && tutarCols.length > 0) valueCol = tutarCols[0]
  }
  if (!valueCol || !data.length) return null

  // Döviz-native: değer döviz bazlı string ("76.677 ₺  +  415.027 €") ise ham göster, toplama/suffix uygulama
  const _rawVal = data.length === 1 ? data[0][valueCol] : null
  const _isDvzStr = typeof _rawVal === 'string' && isNaN(Number(_rawVal)) && _rawVal.trim() !== ''

  // Ozel hesap mantigi: kpi_compute flag
  let value
  const kpiCompute = config?.kpi_compute
  if (kpiCompute === 'karlilik_yuzde') {
    const satisCol = Object.keys(data[0]).find(k => k.toUpperCase() === 'SATIS')
    const alisCol = Object.keys(data[0]).find(k => k.toUpperCase() === 'ALIS')
    if (satisCol && alisCol) {
      const satis = data.reduce((s, r) => s + (Number(r[satisCol]) || 0), 0)
      const alis = data.reduce((s, r) => s + (Number(r[alisCol]) || 0), 0)
      value = satis > 0 ? (satis - alis) / satis * 100 : 0
    } else {
      value = data.reduce((sum, row) => sum + (Number(row[valueCol]) || 0), 0)
    }
  } else if (kpiCompute === 'oran_yuzde') {
    // weighted: SUM(pay) / SUM(payda) * 100
    const payCol = config?.oran_pay || 'KAR'
    const paydaCol = config?.oran_payda || 'MALIYET'
    const pay = data.reduce((s, r) => s + (Number(r[payCol]) || 0), 0)
    const payda = data.reduce((s, r) => s + (Number(r[paydaCol]) || 0), 0)
    value = payda > 0 ? (pay / payda) * 100 : 0
  } else if (kpiCompute === 'son_fiyat') {
    // Filter sonrasi tek satir ise direkt deger, coklu satir ise ortalama
    if (data.length === 1) {
      value = Number(data[0][valueCol]) || 0
    } else {
      const vals = data.map(r => Number(r[valueCol]) || 0).filter(v => v > 0)
      value = vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : 0
    }
  } else {
    value = data.reduce((sum, row) => sum + (Number(row[valueCol]) || 0), 0)
  }
  if (_isDvzStr) value = _rawVal
  const formatted = formatNumber(value, noDec)

  const comparison = config?.comparison
  let changePercent = null
  let changeLabel = ''

  if (comparison && data.length > 1) {
    const mid = Math.floor(data.length / 2)
    const current = data.slice(mid).reduce((s, r) => s + (Number(r[valueCol]) || 0), 0)
    const previous = data.slice(0, mid).reduce((s, r) => s + (Number(r[valueCol]) || 0), 0)
    if (previous > 0) {
      changePercent = ((current - previous) / previous * 100).toFixed(1)
      changeLabel = comparison.label || 'onceki doneme gore'
    }
  }

  return (
    <div onClick={onClick} title={onClick ? 'Seferleri gör' : undefined}
      className={`flex flex-col items-center justify-center h-full p-4 ${onClick ? 'cursor-pointer hover:bg-blue-50/40 dark:hover:bg-blue-900/10 rounded-lg transition-colors' : ''}`}>
      <span className="text-sm text-gray-500 mb-1">{title}</span>
      <span className={`${_isDvzStr ? 'text-xl leading-tight text-center' : 'text-3xl'} font-bold text-gray-800 dark:text-gray-100`}>{config?.prefix || ''}{formatted}{_isDvzStr ? '' : (currencyMode === 'tl' && config?.suffix ? ' ₺' : (config?.suffix || ''))}</span>
      {changePercent !== null && (
        <div className={`flex items-center gap-1 mt-2 text-sm ${Number(changePercent) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          <span>{Number(changePercent) >= 0 ? '▲' : '▼'} %{Math.abs(changePercent)}</span>
          <span className="text-gray-400 text-xs">{changeLabel}</span>
        </div>
      )}
    </div>
  )
}


function HierarchicalTable({ data, config, widgetId, onCrossFilter }) {
  const groupBy = config?.expandable_group_by || []
  const columns = config?.columns || (data.length > 0 ? Object.keys(data[0]) : [])
  // groupBy kolonlari haricindeki kolonlar leaf seviyede gosterilir
  const leafColumns = columns.filter(c => !groupBy.includes(c))
  const [expanded, setExpanded] = useState(() => ({}))   // "YIL|2026" / "YIL|2026/AY|Haziran" -> bool

  const toggle = (key) => setExpanded(p => ({ ...p, [key]: !p[key] }))

  // veriyi hiyerarsik agac olustur
  // tree: { [g0val]: { rows: [], children: { [g1val]: { rows: [], children: {} } } } }
  const tree = (() => {
    const root = { rows: [], children: {} }
    data.forEach(r => {
      let node = root
      groupBy.forEach((g, i) => {
        const v = r[g] ?? '(bos)'
        if (!node.children[v]) node.children[v] = { rows: [], children: {}, _key: g, _val: v }
        node = node.children[v]
      })
      node.rows.push(r)
    })
    return root
  })()

  // Numeric kolon mu (toplam alinabilir mi)?
  const isNumeric = (col) => data.length > 0 && typeof data[0][col] === 'number'
  const numericLeafCols = leafColumns.filter(isNumeric)

  // Grup icin alt toplamlar
  const sumGroup = (rows) => {
    const t = {}
    numericLeafCols.forEach(c => { t[c] = rows.reduce((a, r) => a + (Number(r[c]) || 0), 0) })
    return t
  }
  // Bir agac dugumunun altindaki tum yapraklari topla
  const collectLeaves = (node) => {
    if (node.rows.length > 0) return node.rows
    return Object.values(node.children).flatMap(collectLeaves)
  }

  const formatNum = (v) => {
    if (typeof v !== 'number') return v
    return v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  // Renkler: yil koyu, ay orta, sefer aciksı
  const groupBgs = ['bg-blue-100 dark:bg-blue-900/40', 'bg-blue-50 dark:bg-blue-900/20']

  const renderNode = (node, depth, path) => {
    if (depth >= groupBy.length) {
      // leaf: gercek satirlar
      return node.rows.map((r, i) => (
        <tr key={`${path}_${i}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
          onClick={() => onCrossFilter && r.UNVAN && onCrossFilter('UNVAN', r.UNVAN)}>
          <td className="px-2 py-1 text-[10px] text-gray-300" style={{ paddingLeft: `${(groupBy.length + 1) * 16}px` }}></td>
          {leafColumns.map(c => (
            <td key={c} className={`px-2 py-1 text-[11px] ${isNumeric(c) ? 'text-right tabular-nums' : ''} text-gray-700 dark:text-gray-200 whitespace-nowrap`}>
              {isNumeric(c) ? formatNum(r[c]) : stripMidnight(r[c] ?? '')}
            </td>
          ))}
        </tr>
      ))
    }
    // grup seviyesi
    return Object.entries(node.children)
      .sort(([a], [b]) => {
        // YIL desc, AY: Aralik..Ocak (yeniden eskiye) — basit: orjinal sıra
        const an = Number(a), bn = Number(b)
        if (!isNaN(an) && !isNaN(bn)) return bn - an  // yıl desc
        return 0
      })
      .map(([val, child]) => {
        const key = `${path}|${val}`
        const isOpen = expanded[key]
        const leaves = collectLeaves(child)
        const sums = sumGroup(leaves)
        return (
          <React.Fragment key={key}>
            <tr className={`${groupBgs[depth] || 'bg-gray-100 dark:bg-gray-800'} font-semibold cursor-pointer`}
              onClick={() => toggle(key)}>
              <td className="px-2 py-1 text-[11px] text-gray-700 dark:text-gray-100" style={{ paddingLeft: `${depth * 16 + 4}px` }}>
                <span className="inline-block w-3 text-center">{isOpen ? '▾' : '▸'}</span>
                {val} <span className="text-gray-400 text-[9px] font-normal">({leaves.length} sefer)</span>
              </td>
              {leafColumns.map(c => (
                <td key={c} className={`px-2 py-1 text-[11px] ${isNumeric(c) ? 'text-right tabular-nums font-semibold' : 'text-gray-400'} whitespace-nowrap`}>
                  {isNumeric(c) ? formatNum(sums[c]) : ''}
                </td>
              ))}
            </tr>
            {isOpen && renderNode(child, depth + 1, key)}
          </React.Fragment>
        )
      })
  }

  return (
    <div className="h-full overflow-auto bg-white dark:bg-gray-800">
      <table className="w-full text-[11px]">
        <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900 z-10">
          <tr>
            <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase whitespace-nowrap">
              {groupBy.join(' / ')}
            </th>
            {leafColumns.map(c => (
              <th key={c} className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase whitespace-nowrap">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>{renderNode(tree, 0, 'root')}</tbody>
      </table>
    </div>
  )
}


function DataTable({ data, config, widgetId, dashId, onCrossFilter, onDrillDown, title, dateFrom, dateTo, showMode }) {
  // Hiyerarsik tablo (yil/ay vb. ile gruplandirilmis)
  if (config?.expandable_group_by && config.expandable_group_by.length > 0) {
    return <HierarchicalTable data={data} config={config} widgetId={widgetId} onCrossFilter={onCrossFilter} />
  }
  // Miktar/Tutar mode'a gore kolon seti — config'te columns_miktar/columns_tutar varsa kullan
  const baseColumns = config?.columns || (data.length > 0 ? Object.keys(data[0]) : [])
  const tumColumns = (showMode === 'tutar' && config?.columns_tutar) ? config.columns_tutar
                : (showMode === 'miktar' && config?.columns_miktar) ? config.columns_miktar
                : baseColumns
  // Her satirda AYNI degeri tasiyan metin kolonu tabloda yer israfi:
  // "Yakit Fiyatlari"nda ISTASYON uc satirda da "Altinekin" yaziyor ve
  // Son Satis / Fark kolonlarini gorunmez alana itiyordu. Kolonu tablodan
  // cikarip basligin yanina rozet olarak koyuyoruz. (Disa aktarim etkilenmez;
  // o DashboardView'da ham veriden yapiliyor.)
  const sabitKolonlar = useMemo(() => {
    if (!data || data.length < 2 || tumColumns.length < 3) return []
    return tumColumns.filter(c => {
      const ilk = data[0]?.[c]
      if (ilk == null || ilk === '' || typeof ilk === 'number') return false
      const s = String(ilk).trim()
      if (s === '' || s.length > 40) return false
      return data.every(r => String(r?.[c] ?? '').trim() === s)
    })
  }, [data, tumColumns])
  const columns = sabitKolonlar.length && sabitKolonlar.length < tumColumns.length
    ? tumColumns.filter(c => !sabitKolonlar.includes(c))
    : tumColumns
  const PAGE_SIZE = config?.page_size || 25

  const storageKey = widgetId ? `colWidths_${widgetId}` : null

  const [sortCol, setSortCol] = useState(config?.default_sort?.column || null)
  const [sortDir, setSortDir] = useState(config?.default_sort?.dir || 'asc')
  const [gecikmeFilter, setGecikmeFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [selectedRow, setSelectedRow] = useState(null)
  const [colWidths, setColWidths] = useState(() => {
    if (!storageKey) return {}
    try { return JSON.parse(localStorage.getItem(storageKey) || '{}') } catch { return {} }
  })
  const [detayData, setDetayData] = useState(null)
  const [detayLoading, setDetayLoading] = useState(false)
  const [detayUnvan, setDetayUnvan] = useState(null)
  const [detayDevir, setDetayDevir] = useState(0)
  const [detayOzet, setDetayOzet] = useState(null)  // tum mod: istasyon bazli bakiye ozeti
  const [detayGuncelleme, setDetayGuncelleme] = useState(null)  // snapshot guncelleme zamani (ISO)
  const [detayTarihBas, setDetayTarihBas] = useState('')
  const [detayTarihBit, setDetayTarihBit] = useState('')
  // Fatura detay (master-detail): faturaya tiklayinca alt tabloda kalemleri
  const [faturaNo, setFaturaNo] = useState(null)
  const [faturaData, setFaturaData] = useState([])
  const [faturaLoading, setFaturaLoading] = useState(false)

  const saveColWidths = (newWidths) => {
    setColWidths(newWidths)
    if (storageKey) localStorage.setItem(storageKey, JSON.stringify(newWidths))
  }

  // Bakiye tablosu icin cari detay yukle
  const isBakiyeTable = title && (title.toLowerCase().includes('bakiye') || title.toLowerCase().includes('cari'))
  const isMusteriTable = title && title.toLowerCase().includes('musteri')
  const isFaturaTable = title && title.toLowerCase().includes('fatura')
  // Son Cari Hareketler: inline detay yerine alt BakiyeWidget'a sec
  const isSonCariHareketler = title && title.toLowerCase().includes('son cari')
  const [lojCari, setLojCari] = useState(null)  // lojistik cari detay modali (PG'den)
  const [lojArac, setLojArac] = useState(null)  // lojistik araç işlem dökümü modali (plakaya tiklaninca)
  const [lojPersonel, setLojPersonel] = useState(null)  // Personel Al-Sat: sorumluya tıkla -> o personelin seferleri
  const [lojVade, setLojVade] = useState(null)  // Vade Takip: cariye tiklaninca açık evrak detayi
  const [petrolCari, setPetrolCari] = useState(null)  // petrol cari detay modali (3 istasyon)
  const [firmaCari, setFirmaCari] = useState(null)  // market tenant cari detay (config.cari_detay_modal)
  const [petrolSatis, setPetrolSatis] = useState(null)  // petrol musteri SATIS detay modali (POMPA)
  const [petrolAlis, setPetrolAlis] = useState(null)  // karlilik: urunun son alislari modali
  const [musteriDetay, setMusteriDetay] = useState(null)
  const [musteriDetayLoading, setMusteriDetayLoading] = useState(false)
  const [musteriDetayData, setMusteriDetayData] = useState([])
  const [musteriCollapsed, setMusteriCollapsed] = useState({})
  const [musteriSortDir, setMusteriSortDir] = useState('desc')

  const loadMusteriDetay = async (unvan) => {
    if (musteriDetay === unvan) { setMusteriDetay(null); setMusteriDetayData([]); return }
    setMusteriDetay(unvan)
    setMusteriDetayLoading(true)
    try {
      const dsId = config?.datasource_id || 6
      const resp = await fetch(`/api/musteri-detay/${encodeURIComponent(unvan)}?datasource_id=${dsId}`)
      const d = await resp.json()
      setMusteriDetayData(d.data || [])
    } catch { setMusteriDetayData([]) }
    setMusteriDetayLoading(false)
  }
  // "Tum" istasyon modu: widget verisinde birden fazla ISTASYON varsa cari detayi 3 sube birlesik cek
  const isMultiIstasyon = useMemo(
    () => [...new Set((data || []).map(r => r.ISTASYON).filter(Boolean))].length > 1,
    [data])
  const loadCariDetay = async (unvan, tarihBas, tarihBit) => {
    if (detayUnvan === unvan && !tarihBas && !tarihBit) { setDetayUnvan(null); setDetayData(null); setDetayOzet(null); return }
    setDetayUnvan(unvan)
    setDetayLoading(true)
    try {
      const dsId = config?.datasource_id || 6
      // UNVAN query param (unvan_q) — path'teki nokta/ozel karakter IIS'te 404 veriyor
      let url = `/api/cari-detay/_?datasource_id=${dsId}&unvan_q=${encodeURIComponent(unvan)}`
      if (isMultiIstasyon) url += `&tum=true`
      if (tarihBas) url += `&tarih_bas=${tarihBas}`
      if (tarihBit) url += `&tarih_bit=${tarihBit}`
      const resp = await fetch(url)
      const d = await resp.json()
      setDetayData(d.data || [])
      setDetayDevir(d.devir_bakiye || 0)
      setDetayOzet(d.istasyon_ozet || null)
      setDetayGuncelleme(d.guncelleme || null)
    } catch { setDetayData([]); setDetayDevir(0); setDetayOzet(null); setDetayGuncelleme(null) }
    setDetayLoading(false)
  }

  // Fatura kalemlerini yukle (master-detail) — ayni faturaya tekrar tikla = kapat
  const loadFaturaDetay = async (fatno) => {
    if (faturaNo === fatno) { setFaturaNo(null); setFaturaData([]); return }
    setFaturaNo(fatno)
    setFaturaLoading(true)
    try {
      const dsId = config?.datasource_id || 7
      const url = `/api/fatura-detay/_?datasource_id=${dsId}&fatno_q=${encodeURIComponent(fatno)}`
      const resp = await fetch(url)
      const d = await resp.json()
      setFaturaData(d.data || [])
    } catch { setFaturaData([]) }
    setFaturaLoading(false)
  }

  const startResize = (e, col) => {
    e.preventDefault()
    e.stopPropagation()
    const startX = e.clientX
    const startW = colWidths[col] || e.target.parentElement.offsetWidth

    const onMouseMove = (ev) => {
      const diff = ev.clientX - startX
      const newWidths = { ...colWidths, [col]: Math.max(40, startW + diff) }
      setColWidths(newWidths)
    }
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      // Bitince kaydet
      setColWidths(prev => { if (storageKey) localStorage.setItem(storageKey, JSON.stringify(prev)); return prev })
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  // Gecikme filtresi (varsa)
  const gecikmeFiltered = useMemo(() => {
    if (!config?.gecikme_filter || gecikmeFilter === 'all') return data
    return data.filter(row => {
      const g = Number(row.GECIKME)
      if (isNaN(g)) return false
      if (gecikmeFilter === 'lt3') return g < 3
      if (gecikmeFilter === '3to10') return g >= 3 && g <= 10
      if (gecikmeFilter === 'gt10') return g > 10
      return true
    })
  }, [data, gecikmeFilter, config?.gecikme_filter])

  // Arama
  const searched = useMemo(() => {
    if (!search.trim()) return gecikmeFiltered
    const q = search.toLowerCase()
    return gecikmeFiltered.filter(row =>
      columns.some(col => String(row[col] ?? '').toLowerCase().includes(q))
    )
  }, [gecikmeFiltered, search, columns])

  // Siralama
  const sorted = useMemo(() => {
    if (!sortCol) return searched
    return [...searched].sort((a, b) => {
      const va = a[sortCol], vb = b[sortCol]
      if (va == null && vb == null) return 0
      if (va == null) return 1
      if (vb == null) return -1
      if (typeof va === 'number' && typeof vb === 'number') return sortDir === 'asc' ? va - vb : vb - va
      return sortDir === 'asc'
        ? String(va).localeCompare(String(vb), 'tr')
        : String(vb).localeCompare(String(va), 'tr')
    })
  }, [searched, sortCol, sortDir])

  // Sayfalama
  const totalPages = Math.ceil(sorted.length / PAGE_SIZE)
  const paged = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortCol(col)
      setSortDir('asc')
    }
    setPage(0)
  }

  const handleRowClick = (row, col) => {
    // Firma cari detay penceresi (orn. Aksantas Bakiye/Musteri Raporu): ozet + son islemler + hareketler
    if (config?.cari_detay_modal) {
      const _cu = row.UNVAN ?? row.MUSTERI
      if (typeof _cu === 'string' && _cu.trim()) { setFirmaCari(_cu.trim()); return }
    }
    // Fatura tablosu: cross-filter YAPMA (ust tablo filtrelenmesin), alt tabloda kalemleri ac
    if (isFaturaTable && row.FATNO != null) {
      setSelectedRow(row)
      loadFaturaDetay(row.FATNO)
      return
    }
    // LOJISTIK widget'lari (id 276-499 VEYA lojistik dashboard 11/14): cari detayi
    // LOJISTIK modalindan (PG) acilir; filtre/petrol paneli ACILMAZ.
    if ((widgetId >= 276 && widgetId <= 499) || dashId === 11 || dashId === 14) {
      // Personel Al-Sat: sorumluya (KAYDEDEN e-postası) tıkla -> o personelin seferleri modalı
      if (row.KAYDEDEN && String(row.KAYDEDEN).includes('@')) { setLojPersonel(String(row.KAYDEDEN)); return }
      const _unvan = row.UNVAN ?? row.MUSTERI ?? row.CARI ?? row.musteri ?? row.cari
      if (typeof _unvan === 'string' && _unvan.trim()) {
        // Vade Takip tablosu (w512 Yurtiçi / w513 Yurtdışı): cari -> açık evrak/vade detayı
        if (widgetId === 512 || widgetId === 513) { setLojVade(_unvan.trim()); return }
        setLojCari(_unvan.trim()); return
      }
      // Araç gider/gelir tablosu: PLAKA (Çekici) hücresi -> aracın TÜM işlem dökümü (tüm zaman)
      const _plaka = row.PLAKA ?? row.plaka ?? row.CEKICI ?? row.cekici
      if (typeof _plaka === 'string' && _plaka.trim() && _plaka.trim() !== '-') { setLojArac(_plaka.trim()); return }
    }
    // POCAN PETROL (dashboard 8): UNVAN'li satira tiklaninca MODAL acilir (filtre yok).
    // Musteri Raporu tablolari -> SATIS detayi (pompa islemleri); Bakiye Raporu -> cari ekstre.
    if (dashId === 8) {
      const _punvan = row.UNVAN ?? row.MUSTERI ?? row.CARI
      if (typeof _punvan === 'string' && _punvan.trim()) {
        if (isMusteriTable) { setPetrolSatis(_punvan.trim()); return }
        setPetrolCari(_punvan.trim()); return
      }
      // Karlilik: STOKADI'li satira (Stok Envanter vb.) tiklaninca o urunun SON ALISLARI
      if (typeof row.STOKADI === 'string' && row.STOKADI.trim()) {
        setPetrolAlis(row.STOKADI.trim()); return
      }
    }
    // Son Cari Hareketler: alttaki Cari Bakiye Tablosu (BakiyeWidget) o cariye scroll/select etsin
    if (isSonCariHareketler && row.UNVAN) {
      window.dispatchEvent(new CustomEvent('cari-selected', {
        detail: { BLCARI: row.BLCARI, UNVAN: row.UNVAN, datasourceId: config?.datasource_id || 6 }
      }))
      return
    }
    // Musteri tablosu — cross-filter ile alt tabloyu filtrele
    if (isMusteriTable && row.UNVAN && onCrossFilter) {
      onCrossFilter('UNVAN', row.UNVAN)
      return
    }
    // Bakiye tablosu ise cari detay goster
    if (isBakiyeTable && row.UNVAN) {
      setSelectedRow(row)
      setDetayTarihBas(dateFrom || '')
      setDetayTarihBit(dateTo || '')
      loadCariDetay(row.UNVAN, dateFrom || '', dateTo || '')
      return
    }
    if (onCrossFilter && col && row[col] !== undefined) {
      // Eger tiklanan kolon SAYISAL ise, satirdaki ilk kategori kolonuyla filtrele
      // (orn: Alis Fiyati hucresine tikladiginda STOKADI ile filtrele)
      const val = row[col]
      let filterCol = col
      let filterVal = val
      const isNumeric = typeof val === 'number' || (val !== null && val !== '' && !isNaN(Number(val)))
      if (isNumeric) {
        const kategoriKolonlari = ['STOKADI', 'URUN', 'GRUP', 'STOKKOD', 'MUSTERI', 'UNVAN', 'PLAKA', 'CARI', 'NAME', 'AD']
        for (const k of kategoriKolonlari) {
          if (k in row && row[k] != null && row[k] !== '') {
            filterCol = k
            filterVal = row[k]
            break
          }
        }
      }
      setSelectedRow(row)
      onCrossFilter(filterCol, filterVal)
    }
  }

  // Musteri Islemleri ozel modu: tek UNVAN + TARIH varsa Yil/Ay/Gun hiyerarsik gosterim
  const isMusteriIslem = title && title.toLowerCase().includes('islem')
  const uniqueUnvans = isMusteriIslem ? [...new Set(data.map(r => r.UNVAN).filter(Boolean))] : []
  const hasTarih = data.length > 0 && 'TARIH' in data[0]
  if (isMusteriIslem && hasTarih && uniqueUnvans.length === 1) {
    return <MusteriIslemHierarchy unvan={uniqueUnvans[0]} data={data} title={title} />
  }

  // Kullanici tarafindan secilen tablo boyutlari
  const sz = getTableSizes()

  return (
    <div className="flex flex-col h-full">
      {/* Arama + Filtre + Bilgi */}
      <div className="flex items-center gap-2 px-3 py-1.5 border-b bg-gray-50/50 flex-shrink-0">
        <div className="relative flex-1">
          <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-300" size={12} />
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0) }}
            placeholder="Tabloda ara..."
            className="w-full pl-7 pr-2 py-1 text-xs border rounded bg-white focus:ring-1 focus:ring-primary-500 outline-none"
          />
        </div>
        {config?.gecikme_filter && (
          <div className="flex items-center gap-1">
            {[
              { k: 'all', label: 'Tumu' },
              { k: 'lt3', label: '< 3 gun' },
              { k: '3to10', label: '3-10 gun', cls: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
              { k: 'gt10', label: '> 10 gun', cls: 'bg-red-100 text-red-800 border-red-300' },
            ].map(b => (
              <button key={b.k} onClick={() => { setGecikmeFilter(b.k); setPage(0) }}
                className={`text-[10px] px-2 py-0.5 border rounded ${
                  gecikmeFilter === b.k
                    ? (b.cls ? b.cls + ' ring-1 ring-offset-1' : 'bg-primary-100 text-primary-700 border-primary-300 ring-1 ring-offset-1')
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'
                }`}>{b.label}</button>
            ))}
          </div>
        )}
        {sabitKolonlar.map(c => (
          <span key={c} title={`${config?.column_labels?.[c] || c} — tum satirlarda ayni`}
            className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 whitespace-nowrap">
            {String(data[0][c]).trim()}
          </span>
        ))}
        <span className="text-[10px] text-gray-400">{sorted.length} kayit</span>
        {config?.info_text && (
          <div className="relative group">
            <button className="w-5 h-5 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 text-[11px] font-semibold flex items-center justify-center" title="Bilgi">i</button>
            <div className="absolute right-0 top-6 z-50 hidden group-hover:block w-64 p-3 bg-gray-800 text-white text-[11px] rounded shadow-lg whitespace-pre-line">
              {config.info_text}
            </div>
          </div>
        )}
      </div>

      {/* Tablo */}
      <div className="flex-1 overflow-auto">
        <table className="text-sm" style={{ tableLayout: 'fixed', minWidth: '100%' }}>
          <colgroup>
            {columns.map(col => (
              <col key={col} style={{ width: colWidths[col] ? `${colWidths[col]}px` : 'auto' }} />
            ))}
          </colgroup>
          <thead className="sticky top-0 z-20">
            <tr>
              {columns.map((col, colIdx) => (
                <th
                  key={col}
                  className={`bg-gray-50 dark:bg-gray-700 ${typeof (data?.[0]?.[col]) === 'number' ? 'text-right' : 'text-left'} ${sz.padX} py-2 font-medium text-gray-500 dark:text-gray-300 border-b dark:border-gray-700 select-none ${config?.wrap_headers ? 'whitespace-normal leading-tight align-bottom break-words' : 'whitespace-nowrap'} relative group ${colIdx === 0 ? 'sticky left-0 z-30 shadow-[2px_0_3px_-1px_rgba(0,0,0,0.06)]' : ''}`}
                  style={{ ...(config?.wrap_headers && !colWidths[col] ? {} : { width: colWidths[col] ? `${colWidths[col]}px` : 'auto' }), fontSize: `${sz.fontPx}px` }}
                >
                  <div className={`flex items-center gap-1 cursor-pointer ${typeof (data?.[0]?.[col]) === 'number' ? 'justify-end' : ''}`} onClick={() => handleSort(col)}>
                    {config.column_labels?.[col] || col}
                    {sortCol === col ? (
                      sortDir === 'asc' ? <FiChevronUp size={12} className="text-primary-600" /> : <FiChevronDown size={12} className="text-primary-600" />
                    ) : (
                      <FiChevronUp size={10} className="text-gray-200" />
                    )}
                  </div>
                  <div
                    onMouseDown={(e) => startResize(e, col)}
                    className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary-400 group-hover:bg-primary-200 transition-colors"
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.map((row, i) => {
              const isSelected = selectedRow === row
              // Gecikme bazli satir renklendirme (opt-in via config)
              let gecikmeBg = ''
              if (config?.gecikme_filter && row.GECIKME != null) {
                const g = Number(row.GECIKME)
                if (!isNaN(g)) {
                  if (g > 10) gecikmeBg = 'bg-red-100/80 dark:bg-red-600/25 hover:bg-red-200/80 dark:hover:bg-red-600/40'
                  else if (g >= 3) gecikmeBg = 'bg-yellow-100/80 dark:bg-yellow-500/25 hover:bg-yellow-200/80 dark:hover:bg-yellow-500/40'
                }
              }
              // Ilk sutun sticky icin satira ozel bg class'i (transparan donmesin)
              let firstColBg = 'bg-white dark:bg-gray-800'
              if (isSelected) firstColBg = 'bg-primary-50 dark:bg-primary-900/40'
              else if (config?.gecikme_filter && row.GECIKME != null) {
                const g = Number(row.GECIKME)
                if (!isNaN(g)) {
                  if (g > 10) firstColBg = 'bg-red-100 dark:bg-red-600/25'
                  else if (g >= 3) firstColBg = 'bg-yellow-100 dark:bg-yellow-500/25'
                }
              }
              return (
                <tr
                  key={i}
                  className={`border-b border-gray-50 cursor-pointer transition-colors ${isSelected ? 'bg-primary-50' : (gecikmeBg || 'hover:bg-blue-50/50')}`}
                  onClick={() => handleRowClick(row, columns[0])}
                >
                  {columns.map((col, colIdx) => {
                    const val = row[col]
                    const isNum = typeof val === 'number'
                    // SON_SEFER: tarih + gecikme rozeti (3-10 gun sari, 10+ kirmizi)
                    let cellIcerik = null
                    if (col === 'SON_SEFER' && val != null && row.SON_SEFER_GUN != null) {
                      const g = Number(row.SON_SEFER_GUN)
                      const rozet = g > 10
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                        : g >= 3
                          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                          : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                      cellIcerik = (
                        <span className="whitespace-nowrap">
                          {String(val)}
                          <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold tabular-nums ${rozet}`}>{g} gün</span>
                        </span>
                      )
                    }
                    // VADE: gecmis vade = kirmizi "X gün gecikmiş", <10 gun kalan = yesil "X gün kaldı"
                    if (col === 'VADE' && val != null && row.KALAN_GUN != null) {
                      const g = Number(row.KALAN_GUN)
                      const gecmis = g < 0
                      const yaklasan = g >= 0 && g < 10
                      const rozet = gecmis
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                        : yaklasan
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                          : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300'
                      const etiket = gecmis ? `${Math.abs(g)} gün gecikmiş` : yaklasan ? `${g} gün kaldı` : `${g} gün`
                      cellIcerik = (
                        <span className="whitespace-nowrap">
                          {String(val)}
                          <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold tabular-nums ${rozet}`}>{etiket}</span>
                        </span>
                      )
                    }
                    return (
                      <td
                        key={col}
                        className={`${sz.padX} ${sz.padY} ${isNum ? 'text-right tabular-nums whitespace-nowrap' : 'truncate'} text-gray-700 dark:text-gray-300 ${colIdx === 0 ? `sticky left-0 z-[1] ${firstColBg} shadow-[2px_0_3px_-1px_rgba(0,0,0,0.06)]` : ''}`}
                        style={{ maxWidth: colWidths[col] ? `${colWidths[col]}px` : undefined, fontSize: `${sz.fontPx}px` }}
                        title={val != null ? String(val) : ''}
                        onClick={(e) => { e.stopPropagation(); handleRowClick(row, col) }}
                      >
                        {cellIcerik != null ? cellIcerik
                          : val == null ? <span className="text-gray-200">-</span> : isNum ? formatNumber(val, dashId === 11 || dashId === 14) : stripMidnight(String(val))}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
          {config?.show_totals && sorted.length > 0 && (
            <tfoot>
              <tr className="font-bold bg-slate-100 dark:bg-gray-700 border-t-2 border-gray-300 dark:border-gray-600">
                {columns.map((col, ci) => {
                  if (ci === 0) return <td key={col} className="px-2 py-1.5 sticky left-0 bg-slate-100 dark:bg-gray-700 whitespace-nowrap">TOPLAM ({sorted.length})</td>
                  const isNum = sorted.some(r => typeof r[col] === 'number')
                  // Fiyat/oran/birim-maliyet kolonları toplanmaz (ortalama/oran anlamsız olur)
                  const noTotal = /FIYAT|YUZDE|ORAN|BIRIM_MAL|_MALIYET|%/.test(String(col).toUpperCase())
                  if (!isNum || noTotal) return <td key={col} className="px-2 py-1.5"></td>
                  const sum = sorted.reduce((s, r) => s + (Number(r[col]) || 0), 0)
                  return <td key={col} className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{formatNumber(sum, dashId === 11 || dashId === 14)}</td>
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Sayfalama */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-3 py-1.5 border-t bg-gray-50/50 flex-shrink-0 text-xs text-gray-500">
          <span>{page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, sorted.length)} / {sorted.length}</span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="p-1 rounded hover:bg-gray-200 disabled:opacity-30">
              <FiChevronLeft size={14} />
            </button>
            <span>{page + 1} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="p-1 rounded hover:bg-gray-200 disabled:opacity-30">
              <FiChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Lojistik cari detay modali — lojistik tablolarinda cariye tiklaninca (PG) */}
      {lojCari && <LojDetayModal type="cari" value={lojCari} onClose={() => setLojCari(null)} />}
      {/* Araç işlem dökümü modali — araç gider tablosunda plakaya tiklaninca (tüm zaman) */}
      {lojArac && <LojDetayModal type="arac" value={lojArac} extra={{ tum: true }} onClose={() => setLojArac(null)} />}
      {lojPersonel && <LojDetayModal type="seferliste" value={lojPersonel} extra={{ mod: 'personel', tt: 'YD' }} onClose={() => setLojPersonel(null)} />}
      {/* Vade / açık evrak detay modali — Vade Takip tablosunda cariye tiklaninca */}
      {lojVade && <LojDetayModal type="vade" value={lojVade} onClose={() => setLojVade(null)} />}
      {petrolCari && <LojDetayModal type="petrol-cari" value={petrolCari} onClose={() => setPetrolCari(null)} />}
      {firmaCari && <FirmaCariModal musteri={firmaCari} onClose={() => setFirmaCari(null)} />}
      {petrolSatis && <LojDetayModal type="petrol-satis" value={petrolSatis} onClose={() => setPetrolSatis(null)} />}
      {petrolAlis && <LojDetayModal type="petrol-alis" value={petrolAlis} onClose={() => setPetrolAlis(null)} />}
      {/* Cari Detay Paneli — bakiye tablosunda satira tiklaninca */}
      {detayUnvan && <CariDetayPanel data={detayData} loading={detayLoading} unvan={detayUnvan}
        devir={detayDevir} ozet={detayOzet} guncelleme={detayGuncelleme}
        tarihBas={detayTarihBas} tarihBit={detayTarihBit}
        onTarihChange={(bas, bit) => { setDetayTarihBas(bas); setDetayTarihBit(bit); loadCariDetay(detayUnvan, bas, bit) }}
        onClose={() => { setDetayUnvan(null); setDetayData(null); setDetayDevir(0); setDetayOzet(null); setDetayGuncelleme(null) }} />}
      {/* Fatura Kalem Paneli — faturaya tiklayinca alt tabloda icindekiler */}
      {faturaNo != null && <FaturaDetayPanel fatno={faturaNo} data={faturaData} loading={faturaLoading}
        onClose={() => { setFaturaNo(null); setFaturaData([]) }} />}
      {/* Musteri Detay Paneli — musteri tablosunda satira tiklaninca */}
      {musteriDetay && !musteriDetayLoading && musteriDetayData.length > 0 && (() => {
        const sorted = [...musteriDetayData].sort((a,b) => musteriSortDir==='desc' ? String(b.TARIH||'').localeCompare(String(a.TARIH||'')) : String(a.TARIH||'').localeCompare(String(b.TARIH||'')))
        const grouped = {}
        sorted.forEach(r => { const t=String(r.TARIH||'').substring(0,10); const y=t.substring(0,4)||'?'; const a=t.substring(0,7)||'?'; if(!grouped[y])grouped[y]={}; if(!grouped[y][a])grouped[y][a]=[]; grouped[y][a].push(r) })
        const ayAd = a => ['','Oca','Sub','Mar','Nis','May','Haz','Tem','Agu','Eyl','Eki','Kas','Ara'][parseInt(a.split('-')[1])||0]||a
        const toggleC = k => setMusteriCollapsed(p=>({...p,[k]:!p[k]}))
        return (
          <div className="border-t-2 border-primary-400 bg-slate-50 flex-shrink-0" style={{maxHeight:'45%',overflow:'auto'}}>
            <div className="bg-slate-700 text-white px-3 py-1.5 flex items-center gap-2 sticky top-0 z-20">
              <span className="text-xs font-medium flex-1">{musteriDetay} — {musteriDetayData.length} islem</span>
              <button onClick={()=>setMusteriSortDir(d=>d==='desc'?'asc':'desc')} className="text-[10px] bg-white/10 border border-white/20 rounded px-1.5 py-0.5 text-white">{musteriSortDir==='desc'?'Yeni→Eski':'Eski→Yeni'}</button>
              <button onClick={()=>{setMusteriDetay(null);setMusteriDetayData([])}} className="text-white/60 hover:text-white">✕</button>
            </div>
            <table className="w-full text-[11px]">
              <thead className="sticky top-8 z-10"><tr className="bg-gray-100 text-gray-600">
                <th className="w-6 px-1"></th><th className="text-left px-2 py-1">Tarih</th><th className="text-left px-2 py-1">Urun</th>
                <th className="text-right px-2 py-1">Miktar</th><th className="text-right px-2 py-1">Tutar</th><th className="text-right px-2 py-1">Fiyat</th>
              </tr></thead>
              <tbody>
                {Object.entries(grouped).sort(([a],[b])=>musteriSortDir==='desc'?b.localeCompare(a):a.localeCompare(b)).map(([yil,aylar])=>{
                  const yC=musteriCollapsed[yil]; const yR=Object.values(aylar).flat()
                  return (<React.Fragment key={yil}>
                    <tr className="bg-slate-200 cursor-pointer hover:bg-slate-300" onClick={()=>toggleC(yil)}>
                      <td className="px-1 text-center">{yC?<FiChevronRight size={11}/>:<FiChevronDown size={11}/>}</td>
                      <td className="px-2 py-1 font-bold" colSpan={2}>{yil} ({yR.length})</td>
                      <td className="px-2 py-1 text-right font-bold">{formatNumber(yR.reduce((s,r)=>s+(r.MIKTAR||0),0))}</td>
                      <td className="px-2 py-1 text-right font-bold">{formatNumber(yR.reduce((s,r)=>s+(r.TUTAR||0),0))}</td><td></td>
                    </tr>
                    {!yC&&Object.entries(aylar).sort(([a],[b])=>musteriSortDir==='desc'?b.localeCompare(a):a.localeCompare(b)).map(([ay,rows])=>{
                      const aK=`${yil}-${ay}`; const aC=musteriCollapsed[aK]
                      return (<React.Fragment key={ay}>
                        <tr className="bg-gray-50 cursor-pointer hover:bg-gray-100" onClick={()=>toggleC(aK)}>
                          <td className="pl-4 text-center">{aC?<FiChevronRight size={10}/>:<FiChevronDown size={10}/>}</td>
                          <td className="px-2 py-0.5 font-semibold pl-4" colSpan={2}>{ayAd(ay)} ({rows.length})</td>
                          <td className="px-2 py-0.5 text-right font-medium">{formatNumber(rows.reduce((s,r)=>s+(r.MIKTAR||0),0))}</td>
                          <td className="px-2 py-0.5 text-right font-medium">{formatNumber(rows.reduce((s,r)=>s+(r.TUTAR||0),0))}</td><td></td>
                        </tr>
                        {!aC&&rows.map((r,i)=>(<tr key={i} className="border-b border-gray-50 hover:bg-blue-50/30">
                          <td></td><td className="px-2 py-0.5 pl-6 whitespace-nowrap">{String(r.TARIH||'').substring(0,10)}</td>
                          <td className="px-2 py-0.5 font-medium text-blue-600">{r.URUN}</td>
                          <td className="px-2 py-0.5 text-right tabular-nums">{formatNumber(r.MIKTAR)}</td>
                          <td className="px-2 py-0.5 text-right tabular-nums">{formatNumber(r.TUTAR)}</td>
                          <td className="px-2 py-0.5 text-right tabular-nums text-gray-400">{formatNumber(r.FIYAT)}</td>
                        </tr>))}
                      </React.Fragment>)
                    })}
                  </React.Fragment>)
                })}
              </tbody>
            </table>
          </div>
        )
      })()}
      {musteriDetay && musteriDetayLoading && <div className="text-center text-gray-400 py-4 text-xs border-t">Yukleniyor...</div>}
    </div>
  )
}


function CariDetayPanel({ data, loading, unvan, devir = 0, ozet = null, guncelleme = null, tarihBas, tarihBit, onTarihChange, onClose }) {
  const [sortCol, setSortCol] = useState('TARIH')
  const [sortDir, setSortDir] = useState('desc')
  const [turFilter, setTurFilter] = useState('')
  const [expanded, setExpanded] = useState({})
  const [localBas, setLocalBas] = useState(tarihBas || '')
  const [localBit, setLocalBit] = useState(tarihBit || '')

  if (loading) return (
    <div className="border-t-2 border-primary-400 bg-slate-50 flex-shrink-0 text-center text-gray-400 py-4 text-xs">Yukleniyor...</div>
  )
  if (!data?.length) return (
    <div className="border-t-2 border-primary-400 bg-slate-50 flex-shrink-0">
      <div className="bg-slate-700 text-white px-3 py-1.5 flex items-center justify-between">
        <span className="text-xs">{unvan}</span>
        <button onClick={onClose} className="text-white/60 hover:text-white">✕</button>
      </div>
      <div className="text-center text-gray-400 py-3 text-xs">Hareket bulunamadi</div>
    </div>
  )

  // "Tum" mod: backend her satira ISTASYON + istasyon-ici KUMULATIF_BAKIYE ekler
  const hasIstasyon = data.some(r => r.ISTASYON)

  // Islem turu listesi
  const turler = [...new Set(data.map(r => r.ISLEM_TURU).filter(Boolean))].sort()

  // Filtrele
  const filtered = turFilter ? data.filter(r => r.ISLEM_TURU === turFilter) : data

  // Sirala
  const sorted = [...filtered].sort((a, b) => {
    const va = a[sortCol], vb = b[sortCol]
    if (va == null) return 1; if (vb == null) return -1
    if (typeof va === 'number' && typeof vb === 'number') return sortDir === 'asc' ? va - vb : vb - va
    return sortDir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va))
  })

  // Bakiye kolonlari (her satir icin):
  //  - _CUM  = KUMULATIF bakiye (onceki donem devri DAHIL, genel)
  //  - _DONEM = DONEM bakiyesi (devirsiz, sadece secili donem icinde yuruyen)
  // Backend her satir icin KUMULATIF_BAKIYE + DONEM_BAKIYE dondurur (kronolojik dogru).
  // Yoksa (eski snapshot) frontend'de TARIH sirasina gore hesapla.
  const hasBackendBakiye = filtered.length > 0 && filtered[0].KUMULATIF_BAKIYE !== undefined
  let withCum
  if (hasBackendBakiye) {
    withCum = sorted.map(r => ({ ...r, _CUM: Number(r.KUMULATIF_BAKIYE) || 0, _DONEM: Number(r.DONEM_BAKIYE ?? r.KUMULATIF_BAKIYE) || 0 }))
  } else {
    const chronological = [...filtered].sort((a, b) => String(a.TARIH || '').localeCompare(String(b.TARIH || '')))
    let cumBakiye = devir || 0, donemBakiye = 0
    const cumMap = new Map(), donemMap = new Map()
    chronological.forEach((r, i) => {
      const net = (r.BORC || 0) - (r.ALACAK || 0)
      cumBakiye += net; donemBakiye += net
      cumMap.set(i, cumBakiye); donemMap.set(i, donemBakiye)
    })
    withCum = sorted.map(r => { const idx = chronological.indexOf(r); return { ...r, _CUM: cumMap.get(idx) || 0, _DONEM: donemMap.get(idx) || 0 } })
  }

  // Yil/Ay gruplama (rows -> {yil: {ay: [...]}})
  const groupYilAy = (rows) => {
    const g = {}
    rows.forEach(r => {
      const tarih = String(r.TARIH || '').substring(0, 10)
      const yil = tarih.substring(0, 4) || '????'
      const ay = tarih.substring(0, 7) || '????-??'
      if (!g[yil]) g[yil] = {}
      if (!g[yil][ay]) g[yil][ay] = []
      g[yil][ay].push(r)
    })
    return g
  }
  // Tum modda once istasyona gore ayir; degilse direkt yil/ay
  const groupedByIst = hasIstasyon
    ? withCum.reduce((acc, r) => { const k = r.ISTASYON || '?'; (acc[k] = acc[k] || []).push(r); return acc }, {})
    : null
  const grouped = hasIstasyon ? null : groupYilAy(withCum)

  const toggleCollapse = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }))

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('desc') }
  }

  const SortIcon = ({ col }) => sortCol === col
    ? (sortDir === 'asc' ? <FiChevronUp size={10} className="text-primary-500" /> : <FiChevronDown size={10} className="text-primary-500" />)
    : <FiChevronUp size={9} className="text-gray-300" />

  const turColor = (tur) => {
    const t = (tur || '').toLowerCase()
    if (t.includes('tahsilat') || t.includes('posgelir')) return 'text-green-600'
    if (t.includes('aln')) return 'text-blue-600'
    if (t.includes('sat') || t.includes('borc')) return 'text-orange-600'
    if (t.includes('virman')) return 'text-purple-600'
    return 'text-gray-600'
  }

  // Yil/Ay agacini render et (prefix ile key cakismasi onlenir; istasyon icinde tekrar kullanilir)
  const renderYilAy = (yilAyGrouped, prefix) =>
    Object.entries(yilAyGrouped).sort(([a], [b]) => sortDir === 'desc' ? b.localeCompare(a) : a.localeCompare(b)).map(([yil, aylar]) => {
      const yk = `${prefix}${yil}`
      const yilCollapsed = !expanded[yk]
      const yilRows = Object.values(aylar).flat()
      const yilBorc = yilRows.reduce((s, r) => s + (r.BORC || 0), 0)
      const yilAlacak = yilRows.reduce((s, r) => s + (r.ALACAK || 0), 0)
      return (
        <React.Fragment key={yk}>
          <tr className="bg-slate-200 cursor-pointer hover:bg-slate-300" onClick={() => toggleCollapse(yk)}>
            <td className="px-1 text-center">{yilCollapsed ? <FiChevronRight size={11} /> : <FiChevronDown size={11} />}</td>
            <td className="px-2 py-1 font-bold text-slate-700" colSpan={2}>{yil} ({yilRows.length} islem)</td>
            <td className="px-2 py-1 text-right font-bold text-red-600">{formatNumber(yilBorc)}</td>
            <td className="px-2 py-1 text-right font-bold text-green-600">{formatNumber(yilAlacak)}</td>
            <td className={`px-2 py-1 text-right font-bold ${yilBorc - yilAlacak > 0 ? 'text-red-700' : 'text-green-700'}`}>{formatNumber(yilBorc - yilAlacak)}</td>
            <td></td>
          </tr>
          {!yilCollapsed && Object.entries(aylar).sort(([a], [b]) => sortDir === 'desc' ? b.localeCompare(a) : a.localeCompare(b)).map(([ay, rows]) => {
            const ayKey = `${yk}-${ay}`
            const ayCollapsed = !expanded[ayKey]
            const ayBorc = rows.reduce((s, r) => s + (r.BORC || 0), 0)
            const ayAlacak = rows.reduce((s, r) => s + (r.ALACAK || 0), 0)
            const ayAd = ['', 'Oca', 'Sub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Agu', 'Eyl', 'Eki', 'Kas', 'Ara'][parseInt(ay.split('-')[1]) || 0] || ay
            return (
              <React.Fragment key={ayKey}>
                <tr className="bg-gray-50 cursor-pointer hover:bg-gray-100" onClick={() => toggleCollapse(ayKey)}>
                  <td className="px-1 text-center pl-4">{ayCollapsed ? <FiChevronRight size={10} /> : <FiChevronDown size={10} />}</td>
                  <td className="px-2 py-0.5 font-semibold text-gray-600 pl-4" colSpan={2}>{ayAd} ({rows.length})</td>
                  <td className="px-2 py-0.5 text-right text-red-500 font-medium">{formatNumber(ayBorc)}</td>
                  <td className="px-2 py-0.5 text-right text-green-500 font-medium">{formatNumber(ayAlacak)}</td>
                  <td className={`px-2 py-0.5 text-right font-medium ${ayBorc - ayAlacak > 0 ? 'text-red-600' : 'text-green-600'}`}>{formatNumber(ayBorc - ayAlacak)}</td>
                  <td></td>
                  <td></td>
                </tr>
                {!ayCollapsed && [...rows].sort((a, b) => sortDir === 'desc'
                    ? String(b.TARIH || '').localeCompare(String(a.TARIH || ''))
                    : String(a.TARIH || '').localeCompare(String(b.TARIH || ''))).map((r, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-blue-50/30">
                    <td></td>
                    <td className="px-2 py-0.5 whitespace-nowrap pl-6">{String(r.TARIH || '').substring(0, 10)}</td>
                    <td className={`px-2 py-0.5 font-medium ${turColor(r.ISLEM_TURU)}`}>{r.ISLEM_TURU}</td>
                    <td className="px-2 py-0.5 text-right tabular-nums text-red-600">{r.BORC > 0 ? formatNumber(r.BORC) : ''}</td>
                    <td className="px-2 py-0.5 text-right tabular-nums text-green-600">{r.ALACAK > 0 ? formatNumber(r.ALACAK) : ''}</td>
                    <td className={`px-2 py-0.5 text-right tabular-nums ${r._DONEM > 0 ? 'text-red-500' : 'text-green-500'}`}>{formatNumber(r._DONEM)}</td>
                    <td className={`px-2 py-0.5 text-right tabular-nums font-medium ${r._CUM > 0 ? 'text-red-700' : 'text-green-700'}`}>{formatNumber(r._CUM)}</td>
                    <td className="px-2 py-0.5 truncate max-w-[200px] text-gray-500" title={r.ACIKLAMA || ''}>{r.ACIKLAMA}</td>
                  </tr>
                ))}
              </React.Fragment>
            )
          })}
        </React.Fragment>
      )
    })

  return (
    <div className="border-t-2 border-primary-400 bg-slate-50 flex-shrink-0" style={{ maxHeight: '55%', overflow: 'auto' }}>
      {/* Header */}
      <div className="bg-slate-700 text-white px-3 py-1.5 flex items-center gap-2 sticky top-0 z-20 flex-wrap">
        <span className="text-xs font-medium">{unvan} — {filtered.length} hareket</span>
        {guncelleme && <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/10 text-white/70" title="Cari hareket anlik goruntusu (snapshot) bu zamanda guncellendi">🕐 {ZAMAN_ONCE(guncelleme)}</span>}
        {devir !== 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded ${devir > 0 ? 'bg-red-500/30' : 'bg-green-500/30'}`}>Devir: {formatNumber(devir)}</span>}
        <div className="flex items-center gap-1 ml-auto">
          <input type="date" value={localBas} onChange={e => setLocalBas(e.target.value)}
            className="text-[10px] bg-white/10 border border-white/20 rounded px-1 py-0.5 text-white w-28" />
          <span className="text-[10px]">-</span>
          <input type="date" value={localBit} onChange={e => setLocalBit(e.target.value)}
            className="text-[10px] bg-white/10 border border-white/20 rounded px-1 py-0.5 text-white w-28" />
          <button onClick={() => onTarihChange(localBas, localBit)}
            className="text-[10px] bg-white/20 hover:bg-white/30 rounded px-2 py-0.5">Filtrele</button>
          {(localBas || localBit) && <button onClick={() => { setLocalBas(''); setLocalBit(''); onTarihChange('','') }}
            className="text-[10px] text-white/50 hover:text-white">Temizle</button>}
        </div>
        <select value={turFilter} onChange={e => setTurFilter(e.target.value)}
          className="text-[10px] bg-white/10 border border-white/20 rounded px-1.5 py-0.5 text-white">
          <option value="">Tum Turler</option>
          {turler.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        {/* Export */}
        {['xlsx','pdf'].map(fmt => (
          <button key={fmt} onClick={async () => {
            try {
              const rows = withCum.map(r => ({
                Tarih: String(r.TARIH||'').substring(0,10),
                'Islem Turu': r.ISLEM_TURU||'',
                Borc: r.BORC||0, Alacak: r.ALACAK||0,
                'Bakiye (Donem)': r._DONEM||0, 'Kumulatif (Genel)': r._CUM||0, Aciklama: r.ACIKLAMA||''
              }))
              const resp = await fetch(`/api/export/${fmt}`, {
                method: 'POST',
                headers: {'Content-Type':'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}`},
                body: JSON.stringify({data: rows, filename: `${unvan}_cari_hareket`, title: `${unvan} - Cari Hareketler`})
              })
              if (resp.ok) {
                const blob = await resp.blob()
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a'); a.href = url
                a.download = `${unvan}_cari_hareket.${fmt}`; a.click()
                URL.revokeObjectURL(url)
              }
            } catch {}
          }} className={`text-[10px] ${fmt==='pdf'?'bg-red-500/30 hover:bg-red-500/50':'bg-white/10 hover:bg-white/20'} border border-white/20 rounded px-1.5 py-0.5 text-white`}>
            {fmt==='pdf'?'PDF':'Excel'}
          </button>
        ))}
        <button onClick={onClose} className="text-white/60 hover:text-white ml-1">✕</button>
      </div>

      {/* Istasyon bazli bakiye ozeti (tum mod) — her istasyon + toplam */}
      {hasIstasyon && ozet && ozet.length > 0 && (
        <div className="flex flex-wrap items-stretch gap-1.5 px-3 py-2 bg-slate-100 border-b border-slate-200 sticky top-8 z-[5]">
          {ozet.map(o => {
            const donemB = (o.bakiye || 0) - (o.devir || 0)   // dönem = devirsiz
            return (
            <div key={o.istasyon} className={`flex flex-col px-2.5 py-1 rounded border min-w-[140px] ${o.hata ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'}`}>
              <span className={`text-[10px] font-semibold ${ISTASYON_COLOR(o.istasyon)}`}>{ISTASYON_LABEL(o.istasyon)}</span>
              {o.hata ? (
                <span className="text-[10px] font-medium text-red-500">erişilemedi</span>
              ) : (
                <>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[9px] text-gray-400">Dönem</span>
                    <span className={`text-xs font-medium tabular-nums ${donemB > 0 ? 'text-red-600' : donemB < 0 ? 'text-green-600' : 'text-gray-500'}`}>{formatNumber(donemB)}</span>
                  </div>
                  <div className="flex items-baseline justify-between gap-2 border-t border-gray-200 mt-0.5 pt-0.5">
                    <span className="text-[9px] text-gray-400">Genel</span>
                    <span className={`text-sm font-bold tabular-nums ${o.bakiye > 0 ? 'text-red-700' : o.bakiye < 0 ? 'text-green-700' : 'text-gray-500'}`}>{formatNumber(o.bakiye)}</span>
                  </div>
                  <span className="text-[9px] text-gray-400 mt-0.5">{o.hareket} hareket</span>
                </>
              )}
            </div>
          )})}
          {(() => {
            const tBakiye = ozet.reduce((s, o) => s + (o.bakiye || 0), 0)
            const tDonem = ozet.reduce((s, o) => s + ((o.bakiye || 0) - (o.devir || 0)), 0)
            const tHareket = ozet.reduce((s, o) => s + (o.hareket || 0), 0)
            return (
              <div className="flex flex-col px-2.5 py-1 rounded bg-slate-700 text-white min-w-[140px]">
                <span className="text-[10px] font-semibold">TOPLAM</span>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[9px] text-white/60">Dönem</span>
                  <span className="text-xs font-medium tabular-nums">{formatNumber(tDonem)}</span>
                </div>
                <div className="flex items-baseline justify-between gap-2 border-t border-white/20 mt-0.5 pt-0.5">
                  <span className="text-[9px] text-white/60">Genel</span>
                  <span className="text-sm font-bold tabular-nums">{formatNumber(tBakiye)}</span>
                </div>
                <span className="text-[9px] text-white/60 mt-0.5">{tHareket} hareket</span>
              </div>
            )
          })()}
        </div>
      )}

      {/* Tablo */}
      <table className="w-full text-[11px]">
        <thead className="sticky top-8 z-10">
          <tr className="bg-gray-100 text-gray-600">
            <th className="w-6 px-1"></th>
            {[
              ['TARIH', 'Tarih', 'text-left'],
              ['ISLEM_TURU', 'Islem Turu', 'text-left'],
              ['BORC', 'Borc', 'text-right'],
              ['ALACAK', 'Alacak', 'text-right'],
              ['_DONEM', 'Bakiye (Donem)', 'text-right'],
              ['_CUM', 'Kumulatif (Genel)', 'text-right'],
              ['ACIKLAMA', 'Aciklama', 'text-left'],
            ].map(([key, label, align]) => (
              <th key={key} className={`${align} px-2 py-1 font-medium cursor-pointer hover:bg-gray-200 select-none`} onClick={() => handleSort(key)}>
                <span className="flex items-center gap-0.5">{label} <SortIcon col={key} /></span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Tek modda devir satiri (tum modda her istasyonun deviri istasyon basliginda) */}
          {!hasIstasyon && devir !== 0 && (
            <tr className="bg-amber-50 border-b-2 border-amber-300 font-bold">
              <td></td>
              <td className="px-2 py-1 text-amber-800" colSpan={2}>DEVIR BAKIYE</td>
              <td className="px-2 py-1 text-right text-red-600">{devir > 0 ? formatNumber(devir) : ''}</td>
              <td className="px-2 py-1 text-right text-green-600">{devir < 0 ? formatNumber(Math.abs(devir)) : ''}</td>
              <td className="px-2 py-1 text-right text-gray-400">-</td>
              <td className={`px-2 py-1 text-right ${devir > 0 ? 'text-red-700' : 'text-green-700'}`}>{formatNumber(devir)}</td>
              <td className="px-2 py-1 text-[10px] text-amber-600">Onceki donemden devir</td>
            </tr>
          )}
          {hasIstasyon
            ? Object.entries(groupedByIst).sort(([a], [b]) => ISTASYON_LABEL(a).localeCompare(ISTASYON_LABEL(b), 'tr')).map(([ist, rows]) => {
                const ik = `IST::${ist}`
                const istCollapsed = !expanded[ik]
                const o = (ozet || []).find(x => ISTASYON_LABEL(x.istasyon) === ISTASYON_LABEL(ist))
                const istBorc = o ? o.borc : rows.reduce((s, r) => s + (r.BORC || 0), 0)
                const istAlacak = o ? o.alacak : rows.reduce((s, r) => s + (r.ALACAK || 0), 0)
                const istBakiye = o ? o.bakiye : (istBorc - istAlacak)
                const istDevir = o ? o.devir : 0
                return (
                  <React.Fragment key={ik}>
                    {/* Istasyon baslik */}
                    <tr className={`cursor-pointer border-b border-slate-300 ${istCollapsed ? 'bg-slate-300/60 hover:bg-slate-300' : 'bg-slate-300 hover:bg-slate-400/50'}`} onClick={() => toggleCollapse(ik)}>
                      <td className="px-1 text-center">{istCollapsed ? <FiChevronRight size={12} /> : <FiChevronDown size={12} />}</td>
                      <td className="px-2 py-1 font-bold" colSpan={2}>
                        <span className={ISTASYON_COLOR(ist)}>● </span>
                        <span className="text-slate-800">{ISTASYON_LABEL(ist)}</span>
                        <span className="text-slate-500 font-normal"> ({rows.length} hareket{istDevir ? ` · devir ${formatNumber(istDevir)}` : ''})</span>
                      </td>
                      <td className="px-2 py-1 text-right font-bold text-red-600">{formatNumber(istBorc)}</td>
                      <td className="px-2 py-1 text-right font-bold text-green-600">{formatNumber(istAlacak)}</td>
                      <td className={`px-2 py-1 text-right font-semibold ${(istBakiye - istDevir) > 0 ? 'text-red-500' : 'text-green-500'}`}>{formatNumber(istBakiye - istDevir)}</td>
                      <td className={`px-2 py-1 text-right font-bold ${istBakiye > 0 ? 'text-red-700' : 'text-green-700'}`}>{formatNumber(istBakiye)}</td>
                      <td></td>
                    </tr>
                    {!istCollapsed && renderYilAy(groupYilAy(rows), `${ik}::`)}
                  </React.Fragment>
                )
              })
            : renderYilAy(grouped, '')}
        </tbody>
      </table>
    </div>
  )
}


// Fatura kalem paneli — faturaya tiklayinca altta icindekiler (master-detail)
function FaturaDetayPanel({ fatno, data, loading, onClose }) {
  const toplam = (data || []).reduce((s, r) => s + (Number(r.TOPLAM) || 0), 0)
  return (
    <div className="border-t-2 border-primary-400 bg-slate-50 dark:bg-gray-800 flex-shrink-0" style={{ maxHeight: '55%', overflow: 'auto' }}>
      <div className="bg-slate-700 text-white px-3 py-1.5 flex items-center gap-2 sticky top-0 z-20">
        <span className="text-xs font-medium">Fatura #{fatno} — {(data || []).length} kalem</span>
        {!loading && data?.length > 0 && <span className="text-[10px] ml-auto bg-white/10 rounded px-1.5 py-0.5">Toplam: {formatNumber(toplam)}</span>}
        <button onClick={onClose} className={`text-white/60 hover:text-white ${loading || !data?.length ? 'ml-auto' : ''}`}>✕</button>
      </div>
      {loading ? (
        <div className="p-4 text-center text-gray-400 dark:text-gray-500 text-xs">Yukleniyor...</div>
      ) : !data?.length ? (
        <div className="p-4 text-center text-gray-400 dark:text-gray-500 text-xs">Kalem bulunamadi</div>
      ) : (
        <table className="w-full text-[11px]">
          <thead className="sticky top-8 z-10">
            <tr className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              <th className="text-left px-2 py-1 font-medium w-10">#</th>
              <th className="text-left px-2 py-1 font-medium">Urun</th>
              <th className="text-left px-2 py-1 font-medium">Birim</th>
              <th className="text-right px-2 py-1 font-medium">Adet</th>
              <th className="text-right px-2 py-1 font-medium">Br.Fiyat</th>
              <th className="text-right px-2 py-1 font-medium">KDV%</th>
              <th className="text-right px-2 py-1 font-medium">Tutar</th>
              <th className="text-right px-2 py-1 font-medium">Toplam</th>
            </tr>
          </thead>
          <tbody>
            {data.map((r, i) => (
              <tr key={i} className="border-b border-gray-50 dark:border-gray-700 hover:bg-blue-50/30 dark:hover:bg-blue-900/20">
                <td className="px-2 py-0.5 text-gray-400">{r.SIRANO}</td>
                <td className="px-2 py-0.5 text-gray-700 dark:text-gray-300">{r.URUN}</td>
                <td className="px-2 py-0.5 text-gray-500 dark:text-gray-400">{r.BIRIM}</td>
                <td className="px-2 py-0.5 text-right tabular-nums">{formatNumber(r.ADET)}</td>
                <td className="px-2 py-0.5 text-right tabular-nums">{formatNumber(r.BRMFIYAT)}</td>
                <td className="px-2 py-0.5 text-right tabular-nums text-gray-500">{r.KDV_ORANI != null ? r.KDV_ORANI : ''}</td>
                <td className="px-2 py-0.5 text-right tabular-nums">{formatNumber(r.TUTAR)}</td>
                <td className="px-2 py-0.5 text-right tabular-nums font-medium">{formatNumber(r.TOPLAM)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}


function GaugeWidget({ data, config, showMode, widgetId }) {
  // SVG gradient id'si — benzersiz + bosluksuz olmali (label "Gecen Ay" gibi bosluklu olunca
  // url(#leftArc_Gecen Ay) gecersiz olup renkli arc'lar kayboluyordu)
  const gid = `g${widgetId != null ? widgetId : ''}_${String(config?.label || 'x').replace(/[^a-zA-Z0-9]/g, '')}`
  // value_column = bu donem (Bugun/Bu Ay), max_column = onceki donem (Dun/Gecen Ay/Gecen Yil)
  let valueCol = config?.value_column || 'BUGUN'
  let maxCol = config?.max_column || 'DUN'

  if (showMode === 'tutar' && data?.length > 0 && data[0]['BUGUN_TUTAR'] !== undefined) {
    valueCol = 'BUGUN_TUTAR'
    maxCol = 'DUN_TUTAR'
  }

  const value = data.length > 0 ? Number(data[0][valueCol]) || 0 : 0
  const previous = data.length > 0 && maxCol ? Number(data[0][maxCol]) || 0 : 0

  // Karsilastirma yuzdesi: ((bu - onceki) / onceki) * 100
  // previous=0 ise yuzde gosterilmez, sadece deger gosterilir
  let pct = null
  if (previous > 0) pct = ((value - previous) / previous) * 100

  // Yarim daire ibre - 0 = sol uc (-100%), 90 = ust (0%), 180 = sag uc (+100%)
  const p = pct === null ? 0 : Math.max(-100, Math.min(100, pct))
  const gaugeDeg = 90 + (p / 100) * 90
  const cx = 60, cy = 50, r = 38
  const rad = (180 - gaugeDeg) * Math.PI / 180
  const tipX = cx + r * Math.cos(rad)
  const tipY = cy - r * Math.sin(rad)
  // Renk gradasyonu
  const color = pct === null ? '#9ca3af'
              : p <= -50 ? '#dc2626'
              : p <= -20 ? '#ef4444'
              : p < 0 ? '#f59e0b'
              : p < 5 ? '#84cc16'
              : p < 30 ? '#22c55e'
              : '#10b981'
  const arrow = pct === null ? '' : pct > 0 ? '▲' : pct < 0 ? '▼' : '●'
  const sign = pct === null ? '' : pct > 0 ? '+' : ''
  const isMax = pct !== null && Math.abs(pct) >= 100
  const pctTxt = pct === null ? '—'
              : isMax ? `${pct > 0 ? '+' : '-'}%${Math.round(Math.abs(pct))}`
              : `${arrow} ${sign}${p.toFixed(1)}%`

  return (
    <div className="flex flex-col items-center justify-center h-full p-2 gap-0.5">
      {/* Mevcut deger - ust */}
      <span className="text-xl font-bold text-gray-800 dark:text-gray-100 leading-tight">
        {config?.prefix || ''}{formatNumber(value)}{config?.suffix || ''}
      </span>
      {/* Gauge SVG */}
      <svg viewBox="0 0 120 65" className="w-full max-w-[180px]">
        <defs>
          <linearGradient id={`leftArc_${gid}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#dc2626" />
            <stop offset="100%" stopColor="#fbbf24" />
          </linearGradient>
          <linearGradient id={`rightArc_${gid}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#86efac" />
            <stop offset="100%" stopColor="#16a34a" />
          </linearGradient>
        </defs>
        {/* Sol yari arc (kotu) */}
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx} ${cy - r}`}
              fill="none" stroke={`url(#leftArc_${gid})`} strokeWidth="10" strokeLinecap="butt" />
        {/* Sag yari arc (iyi) */}
        <path d={`M ${cx} ${cy - r} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
              fill="none" stroke={`url(#rightArc_${gid})`} strokeWidth="10" strokeLinecap="butt" />
        {/* Orta tick (0%) */}
        <line x1={cx} y1={cy - r - 3} x2={cx} y2={cy - r + 5} stroke="#94a3b8" strokeWidth="1.5" />
        {/* Sol ve sag uclarda 100% etiketleri */}
        <text x={cx - r + 2} y={cy + 8} fontSize="7" fill="#9ca3af" textAnchor="start">-100%</text>
        <text x={cx + r - 2} y={cy + 8} fontSize="7" fill="#9ca3af" textAnchor="end">+100%</text>
        {/* Ibre */}
        {pct !== null && (
          <>
            <line x1={cx} y1={cy} x2={tipX} y2={tipY} stroke="#1f2937" strokeWidth="3" strokeLinecap="round" />
            <circle cx={cx} cy={cy} r="3.5" fill="#1f2937" />
          </>
        )}
      </svg>
      {/* Yuzde - alt */}
      <span className="text-base font-bold leading-none mt-1" style={{ color }}>
        {pctTxt}
      </span>
      {/* Onceki deger - kucuk */}
      {previous > 0 && (
        <span className="text-[10px] text-gray-400">
          {config?.label || 'onceki'}: {formatNumber(previous)}
        </span>
      )}
    </div>
  )
}


// Musteri Islemleri Yil/Ay/Gun hiyerarsik gosterim
function MusteriIslemHierarchy({ unvan, data, title }) {
  const [expanded, setExpanded] = useState({})
  const [search, setSearch] = useState('')
  const [istFilter, setIstFilter] = useState('')
  const toggleC = (k) => setExpanded(p => ({ ...p, [k]: !p[k] }))
  const ayAd = (m) => ['','Oca','Şub','Mar','Nis','May','Haz','Tem','Ağu','Eyl','Eki','Kas','Ara'][parseInt(m)] || m

  // Istasyon kolonu var mi? (Pocan "Tum"/sube modunda backend ISTASYON ekler)
  const istasyonlar = [...new Set(data.map(r => r.ISTASYON).filter(Boolean))].sort()
  const hasIstasyon = istasyonlar.length > 0

  // Filtrele (urun arama + istasyon)
  const filtered = data.filter(r =>
    (!search || (r.URUN || '').toLowerCase().includes(search.toLowerCase())) &&
    (!istFilter || r.ISTASYON === istFilter))

  const sumMiktar = (arr) => arr.reduce((s,r) => s + (Number(r.MIKTAR)||0), 0)
  const sumTutar = (arr) => arr.reduce((s,r) => s + (Number(r.TUTAR)||0), 0)

  const groupYilAy = (arr) => {
    const g = {}
    arr.forEach(r => {
      const t = String(r.TARIH || '').substring(0, 10)
      const yil = t.substring(0, 4) || '?'
      const ay = t.substring(0, 7) || '?'
      if (!g[yil]) g[yil] = {}
      if (!g[yil][ay]) g[yil][ay] = []
      g[yil][ay].push(r)
    })
    return g
  }

  // Yil/Ay/Islem satirlarini rows'a ekle (prefix ile benzersiz collapse key)
  const pushYilAy = (out, grouped, prefix) => {
    Object.entries(grouped).sort(([a],[b]) => b.localeCompare(a)).forEach(([yil, aylar]) => {
      const yk = `${prefix}${yil}`
      const yC = !expanded[yk]
      const yRows = Object.values(aylar).flat()
      out.push(
        <tr key={`y-${yk}`} className="bg-slate-200 dark:bg-slate-700 cursor-pointer hover:bg-slate-300 dark:hover:bg-slate-600" onClick={() => toggleC(yk)}>
          <td className="px-1 text-center">{yC ? <FiChevronRight size={11}/> : <FiChevronDown size={11}/>}</td>
          <td className="px-2 py-1 font-bold text-slate-700 dark:text-slate-200" colSpan={2}>{yil} ({yRows.length} islem)</td>
          <td className="px-2 py-1 text-right font-bold tabular-nums">{formatNumber(sumMiktar(yRows))}</td>
          <td className="px-2 py-1 text-right font-bold tabular-nums">{formatNumber(sumTutar(yRows))}</td>
        </tr>
      )
      if (!yC) {
        Object.entries(aylar).sort(([a],[b]) => b.localeCompare(a)).forEach(([ay, items]) => {
          const aK = `${yk}-${ay}`; const aC = !expanded[aK]
          out.push(
            <tr key={`m-${aK}`} className="bg-gray-50 dark:bg-gray-700/50 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => toggleC(aK)}>
              <td className="pl-4 text-center">{aC ? <FiChevronRight size={10}/> : <FiChevronDown size={10}/>}</td>
              <td className="px-2 py-0.5 font-semibold text-gray-600 dark:text-gray-300 pl-4" colSpan={2}>{ayAd(ay.substring(5))} {yil} ({items.length} islem)</td>
              <td className="px-2 py-0.5 text-right font-medium tabular-nums">{formatNumber(sumMiktar(items))}</td>
              <td className="px-2 py-0.5 text-right font-medium tabular-nums">{formatNumber(sumTutar(items))}</td>
            </tr>
          )
          if (!aC) {
            const sortedItems = [...items].sort((a,b) => String(b.TARIH||'').localeCompare(String(a.TARIH||'')))
            sortedItems.forEach((r, i) => {
              out.push(
                <tr key={`i-${aK}-${i}`} className="border-b border-gray-50 dark:border-gray-700 hover:bg-blue-50/30 dark:hover:bg-blue-900/20">
                  <td></td>
                  <td className="px-2 py-0.5 pl-8 whitespace-nowrap text-gray-600 dark:text-gray-400">{String(r.TARIH||'').substring(0,10)}</td>
                  <td className="px-2 py-0.5 text-gray-700 dark:text-gray-300 truncate">{r.URUN || ''}</td>
                  <td className="px-2 py-0.5 text-right tabular-nums">{formatNumber(r.MIKTAR)}</td>
                  <td className="px-2 py-0.5 text-right tabular-nums">{formatNumber(r.TUTAR)}</td>
                </tr>
              )
            })
          }
        })
      }
    })
  }

  const rows = []
  if (hasIstasyon) {
    // Istasyon -> Yil -> Ay -> Islem (her istasyon kendi toplamiyla bir blok)
    const byIst = filtered.reduce((acc, r) => { const k = r.ISTASYON || '?'; (acc[k] = acc[k] || []).push(r); return acc }, {})
    Object.entries(byIst).sort(([a],[b]) => ISTASYON_LABEL(a).localeCompare(ISTASYON_LABEL(b), 'tr')).forEach(([ist, arr]) => {
      const ik = `IST::${ist}`
      const iC = !expanded[ik]
      rows.push(
        <tr key={`ist-${ik}`} className="bg-slate-300 dark:bg-slate-600 cursor-pointer hover:bg-slate-400/50 dark:hover:bg-slate-500 border-b border-slate-400/40" onClick={() => toggleC(ik)}>
          <td className="px-1 text-center">{iC ? <FiChevronRight size={12}/> : <FiChevronDown size={12}/>}</td>
          <td className="px-2 py-1 font-bold" colSpan={2}>
            <span className={ISTASYON_COLOR(ist)}>● </span>
            <span className="text-slate-800 dark:text-slate-100">{ISTASYON_LABEL(ist)}</span>
            <span className="text-slate-500 dark:text-slate-400 font-normal"> ({arr.length} islem)</span>
          </td>
          <td className="px-2 py-1 text-right font-bold tabular-nums">{formatNumber(sumMiktar(arr))}</td>
          <td className="px-2 py-1 text-right font-bold tabular-nums">{formatNumber(sumTutar(arr))}</td>
        </tr>
      )
      if (!iC) pushYilAy(rows, groupYilAy(arr), `${ik}::`)
    })
  } else {
    pushYilAy(rows, groupYilAy(filtered), '')
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-3 py-1.5 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex-shrink-0">
        <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 truncate">{unvan}</span>
        <div className="relative flex-1">
          <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-300" size={12} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Urun ara..." className="w-full pl-7 pr-2 py-1 text-xs border rounded bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100" />
        </div>
        {hasIstasyon && istasyonlar.length > 1 && (
          <select value={istFilter} onChange={e => setIstFilter(e.target.value)}
            className="text-[10px] border rounded px-1.5 py-1 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100">
            <option value="">Tum Istasyonlar</option>
            {istasyonlar.map(s => <option key={s} value={s}>{ISTASYON_LABEL(s)}</option>)}
          </select>
        )}
        <span className="text-[10px] text-gray-400">{filtered.length} islem</span>
      </div>
      <div className="flex-1 overflow-auto">
        <table className={`w-full ${getTableSizes().font} table-fixed`}>
          <colgroup>
            <col style={{ width: '28px' }} />
            <col style={{ width: '110px' }} />
            <col />
            <col style={{ width: '120px' }} />
            <col style={{ width: '130px' }} />
          </colgroup>
          <thead className="sticky top-0 z-10">
            <tr className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              <th className="px-1"></th>
              <th className="text-left px-2 py-1 font-medium">Tarih</th>
              <th className="text-left px-2 py-1 font-medium">Urun</th>
              <th className="text-right px-2 py-1 font-medium">Miktar</th>
              <th className="text-right px-2 py-1 font-medium">Tutar</th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </div>
    </div>
  )
}
